// DailyDetailDashboard.js - Session Debrief (Layer 3)
// Phase 2: Narrative intelligence components added
// All data loading, derived state, modals, and child components unchanged.

import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { exportPredictions, exportActivities, exportOrders, exportGeneric } from "./components/CSVExport";

// Services — unchanged
import { prepareData, predictionAccuracy } from "./service/PredictionAccuracy";
import prepareOrder from "./service/PrepareOrder";
import buildActivities from "./service/BuildActivity";
import config from "./config";
import axios from "axios";
import { getToken } from "./service/AuthService";

// Components — unchanged
import MarketChart from "./components/TradingCommand/MarketChart";
import EvaluationTimeline from "./components/TradingCommand/EvaluationTimeline";
import EvaluationDetailModal from "./components/TradingCommand/EvaluationDetailModal";
import PositionDetailModal from "./components/TradingCommand/PositionDetailModal";
import ClosedPositions from "./components/TradingCommand/ClosedPositions";
import ActivePositions from "./components/TradingCommand/ActivePositions";
import ActivitySummary from "./components/TradingCommand/ActivitySummary";
import PositionLedger from "./components/PositionLedger/PositionLedger";
import OrderLedgerBuilder from "./components/PositionLedger/OrderLedgerBuilder";
import PredictionChart from "./components/PredictionChart";
import MemoryConfidenceBar from "./components/TradingCommand/MemoryConfidenceBar";
import RegimeDecisionTally from "./components/RegimeDecisionTally";
import ModelConsensusPanel from "./components/ModelConsensusPanel";

import {
  fetchAlpacaAccount,
  fetchOrdersByDate,
  getCacheStatus,
} from "./service/AlpacaService";

// Redux actions — unchanged
import {
  addOrder,
  addPrediction,
  addActivity,
  clearOrder,
  clearPrediction,
  clearActivity,
} from "./service/actions";

// ============================================================================
// DESIGN TOKENS
// ============================================================================
const C = {
  bg:     "#08101e",
  bg1:    "#0c1525",
  bg2:    "#101b2e",
  bg3:    "#141f33",
  border: "#1e2d42",
  border2:"#243548",
  text:   "#e8edf5",
  body:   "#b8c8dc",
  meta:   "#6a7d95",
  green:  "#00e896",
  cyan:   "#00c8ff",
  amber:  "#ffb340",
  red:    "#ff4466",
  blue:   "#4488ff",
  purple: "#9966ff",
};

const CSS_ANIMATIONS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');
  @keyframes orbPulse {
    0%,100% { transform:scale(1); opacity:.15; }
    50%      { transform:scale(3); opacity:0; }
  }
  @keyframes fadeUp {
    from { opacity:0; transform:translateY(12px); }
    to   { opacity:1; transform:translateY(0); }
  }
`;

// ============================================================================
// BRD-2 HELPERS — extract regime_context / regime_summary from a bar
// ============================================================================
function extractRcFromBar(bar) {
  if (!bar) return null;
  const story = bar.eval_agent_story || {};
  const rs    = story.regime_summary  || {};
  const rc    = (story.market_snapshot || {}).regime_context || {};
  if (!Object.keys(rs).length && !Object.keys(rc).length) return null;
  return {
    dayType:          rs.day_type          || rc.day_type          || null,
    dayTypeReason:    rc.day_type_reason   || null,
    volatilityCtx:    rs.volatility_context|| rc.volatility_context|| null,
    volatilityReason: rc.volatility_context_reason || null,
    entryType:        rs.entry_type        || rc.entry_type        || null,
    entryAllowed:     rc.entry_allowed     ?? rs.entry_allowed     ?? null,
    entryBlockReason: rc.entry_block_reason || null,
    priorProceedTaken:rc.prior_proceed_taken ?? false,
    eventDay:         rc.event_day_detected ?? false,
    der:              rc.der               ?? null,
    ci14:             rc.ci_14             ?? null,
    ci6:              rc.ci_6              ?? null,
    rrFloor:          rs.rr_floor          ?? rc.rr_floor          ?? null,
    scaleCap:         rs.scale_cap         ?? rc.scale_cap         ?? null,
    stopFloor:        rs.stop_floor_pct    ?? rc.stop_floor_pct    ?? null,
  };
}

const DAY_TYPE_COLOR = {
  TREND_DAY:        "#00e896",
  STRONG_TREND_DAY: "#00e896",
  EVENT_TREND:      "#00c8ff",
  EVENT_DAY:        "#00c8ff",
  WEAK_TREND_DAY:   "#88d400",
  RANGE_DAY:        "#4488ff",
  SIDEWAYS_DAY:     "#9966ff",
  CHOPPY_DAY:       "#ff4466",
  VOLATILE_DAY:     "#ffb340",
};
const dayTypeColor = dt => DAY_TYPE_COLOR[dt] || "#6a7d95";

// ============================================================================
// SHARED UI PRIMITIVES
// ============================================================================
const Orb = ({ color, size = 8, speed = "3s" }) => (
  <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
    <div style={{
      position: "absolute", inset: 0, borderRadius: "50%", background: color,
      animation: `orbPulse ${speed} ease-in-out infinite`,
    }} />
    <div style={{ position: "absolute", inset: "25%", borderRadius: "50%", background: color }} />
  </div>
);

const Chip = ({ color, children }) => (
  <span style={{
    fontFamily: "'DM Mono',monospace", fontSize: 9, fontWeight: 500,
    color, background: color + "18", border: `1px solid ${color}30`,
    borderRadius: 3, padding: "3px 8px", letterSpacing: ".6px", whiteSpace: "nowrap",
  }}>
    {children}
  </span>
);

const Eyebrow = ({ children }) => (
  <div style={{
    fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta,
    letterSpacing: "2px", textTransform: "uppercase", marginBottom: 12,
  }}>
    {children}
  </div>
);

// ============================================================================
// SESSION CONTEXT HEADER — breadcrumb with passed state from TradeDashboard
// ============================================================================
function SessionContextHeader({ sessionSummary, dateParam, liveBars = [], onBack }) {
  const display = sessionSummary || {
    displayDate: dateParam
      ? new Date(dateParam + "T12:00:00").toLocaleDateString("en-US", {
          month: "long", day: "numeric", year: "numeric",
        })
      : "—",
    sessionType: null,
    sessionColor: null,
    pnl: null,
  };

  // BRD-2: pull regime context from first PROCEED bar with agent story
  const firstProceed = liveBars.find(b =>
    ["PROCEED", "BUY"].includes(b.eval_decision || b.decision) && b.eval_agent_story
  );
  const rc = extractRcFromBar(firstProceed);

  return (
    <div style={{
      background: C.bg1, borderBottom: `1px solid ${C.border}`,
      padding: "14px 32px", display: "flex", alignItems: "center",
      gap: 16, flexWrap: "wrap",
    }}>
      {/* Back link */}
      <a
        href="/trading"
        style={{
          fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta,
          textDecoration: "none", letterSpacing: ".5px",
          display: "flex", alignItems: "center", gap: 6, transition: "color .15s",
        }}
        onClick={e => { e.preventDefault(); onBack ? onBack() : window.history.back(); }}
        onMouseEnter={e => e.currentTarget.style.color = C.cyan}
        onMouseLeave={e => e.currentTarget.style.color = C.meta}
      >
        ← PERFORMANCE RECORD
      </a>

      <span style={{ color: C.border2 }}>·</span>

      <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.text, fontWeight: 500 }}>
        {display.displayDate}
      </span>

      {display.sessionType && (
        <>
          <span style={{ color: C.border2 }}>·</span>
          <Chip color={display.sessionColor || C.cyan}>{display.sessionType}</Chip>
        </>
      )}

      {/* BRD-2 chips */}
      {rc?.dayType && (
        <>
          <span style={{ color: C.border2 }}>·</span>
          <Chip color={dayTypeColor(rc.dayType)}>{rc.dayType.replace(/_/g, " ")}</Chip>
        </>
      )}
      {rc?.volatilityCtx && (
        <Chip color={C.meta}>{rc.volatilityCtx.replace(/_/g, " ")}</Chip>
      )}
      {rc?.eventDay && (
        <Chip color={C.cyan}>EVENT DAY</Chip>
      )}

      {display.pnl != null && (
        <>
          <span style={{ color: C.border2 }}>·</span>
          <span style={{
            fontFamily: "'DM Mono',monospace", fontSize: 13,
            color: display.pnl >= 0 ? C.green : C.red, fontWeight: 500,
          }}>
            {display.pnl >= 0 ? `+$${display.pnl.toFixed(0)}` : `-$${Math.abs(display.pnl).toFixed(0)}`}
          </span>
        </>
      )}
    </div>
  );
}

// ============================================================================
// ZONE 1 — DAILY SCORECARD
// ============================================================================
function DailyScorecard({ liveBars, metrics }) {
  const evaluated = liveBars.filter(b => b.eval_decision || b.decision).length;
  const proceeded = liveBars.filter(b => ["PROCEED", "BUY"].includes(b.eval_decision || b.decision)).length;
  const skipped   = liveBars.filter(b => (b.eval_decision || b.decision) === "SKIP").length;

  const winRate = metrics?.winRate || 0;
  const lifetimeWinRate = 78;
  const aboveLifetime = winRate > lifetimeWinRate;

  const pnl = metrics?.dayPL;
  const pnlStr = pnl != null
    ? (pnl >= 0 ? `+$${pnl.toFixed(0)}` : `-$${Math.abs(pnl).toFixed(0)}`)
    : "—";

  // BRD-2: day type from first PROCEED bar
  const firstProceed = liveBars.find(b =>
    ["PROCEED", "BUY"].includes(b.eval_decision || b.decision) && b.eval_agent_story
  );
  const rc = extractRcFromBar(firstProceed);
  const dtColor = rc?.dayType ? dayTypeColor(rc.dayType) : C.meta;
  const dtLabel = rc?.dayType ? rc.dayType.replace(/_/g, " ") : "—";
  const dtSub   = rc?.volatilityCtx
    ? rc.volatilityCtx.replace(/_/g, " ").toLowerCase()
    : "no regime data";

  const directionalAccuracy = metrics?.directionalAccuracy || 0;

  const cards = [
    {
      num: `${winRate}%`,
      label: "SESSION WIN RATE",
      sub: winRate === 0
        ? "no closed trades"
        : `${aboveLifetime ? "above" : "below"} ${lifetimeWinRate}% lifetime avg · bot direction ${directionalAccuracy}%`,
      color: winRate >= 70 ? C.green : winRate >= 55 ? C.amber : C.red,
    },
    {
      num: metrics?.avgHoldTime ? `${metrics.avgHoldTime}m` : "—",
      label: "AVG HOLD TIME",
      sub: "entry to exit",
      color: C.cyan,
    },
    {
      num: pnlStr,
      label: "SESSION P&L",
      sub: "net realized",
      color: pnl != null ? (pnl >= 0 ? C.green : C.red) : C.meta,
    },
    {
      num: `${evaluated} eval`,
      label: "BOT EVALUATIONS",
      sub: `${proceeded} proceed · ${skipped} skip`,
      color: C.purple,
    },
    {
      num: dtLabel,
      label: "DAY TYPE",
      sub: dtSub,
      color: dtColor,
    },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, margin: "32px 0" }}>
      {cards.map((c, i) => (
        <div key={i} style={{
          background: C.bg1, border: `1px solid ${C.border}`,
          borderRadius: 12, padding: "28px 24px",
          animation: "fadeUp .4s ease both", animationDelay: `${i * 60}ms`,
        }}>
          <div style={{
            fontFamily: "'DM Mono',monospace", fontWeight: 500,
            fontSize: "clamp(18px,2vw,30px)", color: c.color,
            letterSpacing: "-.3px", lineHeight: 1, marginBottom: 12,
          }}>
            {c.num}
          </div>
          <div style={{
            fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta,
            letterSpacing: "1.1px", marginBottom: 6, textTransform: "uppercase",
          }}>
            {c.label}
          </div>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.body, fontWeight: 300 }}>
            {c.sub}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// ZONE 2 — SESSION INTELLIGENCE SUMMARY
// ============================================================================
function SessionIntelligenceSummary({ liveBars, metrics }) {
  const winRate   = metrics?.winRate || 0;
  const trades    = metrics?.closedTrades || 0;
  const isSkipDec    = d => ["SKIP", "SKIP_TRADE"].includes(d);
  const isProceedDec = d => ["PROCEED", "BUY"].includes(d);

  const evaluated = liveBars.filter(b => b.eval_decision || b.decision).length;
  const proceeded = liveBars.filter(b => isProceedDec(b.eval_decision || b.decision)).length;
  const skipped   = liveBars.filter(b => isSkipDec(b.eval_decision || b.decision)).length;

  // BRD-2
  const firstProceed = liveBars.find(b => isProceedDec(b.eval_decision || b.decision) && b.eval_agent_story);
  const rc = extractRcFromBar(firstProceed);
  const dayType = rc?.dayType || null;

  // Orb color — day type first, else outcome
  let orbColor = C.cyan;
  if (dayType && DAY_TYPE_COLOR[dayType]) orbColor = DAY_TYPE_COLOR[dayType];
  else if (winRate >= 70)      orbColor = C.green;
  else if (winRate >= 50) orbColor = C.amber;
  else if (winRate > 0)   orbColor = C.red;

  // Day-type-aware headline prefix
  const dtPrefix = dayType ? ({
    TREND_DAY:        "Trending session",
    STRONG_TREND_DAY: "Strong trend session",
    WEAK_TREND_DAY:   "Weak trend session",
    EVENT_TREND:      "Event trend session",
    EVENT_DAY:        "Event day session",
    RANGE_DAY:        "Range-bound session",
    SIDEWAYS_DAY:     "Sideways session",
    CHOPPY_DAY:       "Choppy session",
    VOLATILE_DAY:     "Volatile session",
  })[dayType] || null : null;

  const headline = (() => {
    const pfx = dtPrefix;
    if (winRate === 0 && trades === 0)
      return `${pfx || "Quiet session"} — no completed trades, market conditions not suitable`;
    if (trades <= 2)
      return `${pfx ? pfx + " —" : "Selective session —"} bot evaluated ${evaluated} setups, proceeded on ${proceeded} only, regime conservative`;
    if (winRate < 50)
      return `${pfx ? pfx + " —" : "Contained session —"} ${trades - Math.round(trades * winRate / 100)} stops triggered as designed, no position exceeded max risk`;
    if (winRate >= 75)
      return `${pfx ? pfx + " —" : "Trending session —"} bot found ${proceeded} high-confidence setups, ${Math.round(trades * winRate / 100)} closed at target`;
    return `${pfx ? pfx + " —" : "Steady session —"} ${trades} trades completed, ${winRate}% win rate, bot operated within normal parameters`;
  })();

  // Memory note
  const lastProceedBar = liveBars.slice().reverse().find(b =>
    ["PROCEED", "BUY"].includes(b.eval_decision || b.decision) && b.eval_agent_story
  );
  const mem = lastProceedBar?.eval_agent_story?.memory_summary
    || lastProceedBar?.eval_agent_story?.memory
    || null;
  const memCount = mem?.stm_cases_used || mem?.stm_count || null;
  const memNote = memCount
    ? `STM matched ${memCount} similar sessions · memory architecture active`
    : "Memory architecture active · pattern recognition informed decisions";

  // BRD-2: bars where entry was blocked
  const blockedBars = liveBars.filter(b =>
    isProceedDec(b.eval_decision || b.decision) &&
    extractRcFromBar(b)?.entryAllowed === false
  );
  const blockReason = blockedBars.length > 0
    ? extractRcFromBar(blockedBars[0])?.entryBlockReason
    : null;

  return (
    <div style={{
      background: C.bg1, border: `1px solid ${C.border}`,
      borderRadius: 14, padding: "28px 32px", marginBottom: 32,
    }}>
      <Eyebrow>Session Intelligence</Eyebrow>

      {/* Orb + headline */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 20 }}>
        <div style={{ paddingTop: 4 }}>
          <Orb color={orbColor} size={10} speed="4s" />
        </div>
        <div style={{
          fontFamily: "'DM Mono',monospace", fontWeight: 500,
          fontSize: "clamp(14px,1.6vw,20px)", color: C.text, lineHeight: 1.4,
        }}>
          {headline}
        </div>
      </div>

      {/* BRD-2: entry blocked notice */}
      {blockedBars.length > 0 && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px", background: C.bg,
          border: `1px solid ${C.amber}30`, borderRadius: 8, marginBottom: 16,
        }}>
          <span style={{ fontSize: 10, color: C.amber, flexShrink: 0 }}>⊘</span>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.body, fontWeight: 300 }}>
            {blockedBars.length} evaluation{blockedBars.length > 1 ? "s" : ""} had entry blocked by regime
            {blockReason ? ` — ${blockReason}` : ""}
          </span>
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: "flex", gap: 24, marginBottom: 20, flexWrap: "wrap" }}>
        {[
          { label: "EVALUATED", val: evaluated, color: C.meta  },
          { label: "PROCEED",   val: proceeded, color: C.cyan  },
          { label: "SKIP",      val: skipped,   color: C.amber },
          { label: "COMPLETED", val: trades,    color: C.green },
        ].map(({ label, val, color }) => (
          <div key={label} style={{
            background: C.bg, border: `1px solid ${C.border}`,
            borderRadius: 8, padding: "10px 18px",
          }}>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta, letterSpacing: "1px", marginBottom: 4 }}>
              {label}
            </div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontWeight: 500, fontSize: 22, color }}>
              {val}
            </div>
          </div>
        ))}

        {/* BRD-2: regime limits pill — only when data exists */}
        {rc && (rc.rrFloor != null || rc.scaleCap != null || rc.stopFloor != null) && (
          <div style={{
            background: C.bg, border: `1px solid ${C.border}`,
            borderRadius: 8, padding: "10px 18px",
          }}>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta, letterSpacing: "1px", marginBottom: 6 }}>
              REGIME LIMITS
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {rc.rrFloor   != null && <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.cyan  }}>RR≥{rc.rrFloor.toFixed(1)}</span>}
              {rc.scaleCap  != null && <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.blue  }}>{rc.scaleCap}× cap</span>}
              {rc.stopFloor != null && <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.amber }}>{(rc.stopFloor * 100).toFixed(1)}% floor</span>}
            </div>
          </div>
        )}
      </div>

      {/* Memory note */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 14px", background: C.bg,
        border: `1px solid ${C.purple}20`, borderRadius: 8,
      }}>
        <span style={{ fontSize: 12, color: C.purple }}>◑</span>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.body, fontWeight: 300 }}>
          {memNote}
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// ============================================================================
// CSS — card fade-in only, no in-card animation
// ============================================================================
const CAROUSEL_CSS = `
  @keyframes cardFadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0);   }
  }
  .trade-card-enter { animation: cardFadeIn .3s ease both; }
`;

// ============================================================================
// FILL RATE STRIP — unfilled PROCEEDs surfaced as a neutral execution metric
// ============================================================================
function FillRateStrip({ liveBars, ledgerPositions }) {
  const isProceed = d => ["PROCEED", "BUY"].includes(d);
  const proceedBars = liveBars.filter(b => isProceed(b.eval_decision || b.decision));
  const total = proceedBars.length;
  if (total === 0) return null;

  const filled = proceedBars.filter(pb => {
    const pbTime = pb.t ? new Date(pb.t).getTime() : null;
    if (!pbTime) return false;
    // Match against equity rows only — one per PROCEED decision
    const equityRows = ledgerPositions?.filter(lp => !lp.option_type) || [];
    return equityRows.some(lp => {
      const lpTime = lp.entry_submitted_at_raw
        ? new Date(lp.entry_submitted_at_raw).getTime() : null;
      return lpTime && Math.abs(pbTime - lpTime) < 90000;
    });
  }).length;

  const unfilled  = total - filled;
  const fillPct   = total > 0 ? Math.round((filled / total) * 100) : 0;

  return (
    <div style={{
      background: C.bg, border: `1px solid ${C.border}`,
      borderRadius: 10, padding: "14px 20px", marginBottom: 16,
      display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap",
    }}>
      {/* Fill rate bar — cyan, neutral */}
      <div style={{ flex: 1, minWidth: 160 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta, letterSpacing: "1px" }}>
            ORDER FILL RATE
          </span>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.cyan, fontWeight: 500 }}>
            {fillPct}%
          </span>
        </div>
        <div style={{ height: 3, background: C.bg3, borderRadius: 2, overflow: "hidden" }}>
          <div style={{ width: `${fillPct}%`, height: "100%", background: C.cyan, borderRadius: 2, transition: "width .6s ease" }} />
        </div>
      </div>

      {/* Stats — all neutral color, no alarm */}
      {[
        { label: "SIGNALS SENT",     val: total,    color: C.body  },
        { label: "ORDERS FILLED",    val: filled,   color: C.green },
        { label: "EXPIRED AT LIMIT", val: unfilled, color: C.body  },
      ].map(({ label, val, color }) => (
        <div key={label} style={{ textAlign: "center" }}>
          <div style={{ fontFamily: "'DM Mono',monospace", fontWeight: 500, fontSize: 18, color }}>
            {val}
          </div>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta, letterSpacing: ".8px", marginTop: 2 }}>
            {label}
          </div>
        </div>
      ))}

      {/* Plain note — no warning tone */}
      {unfilled > 0 && (
        <div style={{
          fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.meta,
          fontWeight: 300, borderLeft: `2px solid ${C.border}`,
          paddingLeft: 12, maxWidth: 260,
        }}>
          {unfilled} signal{unfilled > 1 ? "s" : ""} expired at limit — price moved before fill · execution discipline
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TRADE ICON ROW — persistent overview nav above the card
// ============================================================================
function TradeIconRow({ trades, activeIdx, onSelect }) {
  return (
    <div style={{
      display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap",
    }}>
      {trades.map((t, i) => {
        const pnl       = t.combinedPnl;
        const isWin     = pnl > 0;
        const isDirCorrect = t.isDirectionallyCorrect; // equity hit target, options erased it
        // amber = direction right but net loss; green = net win; red = direction wrong
        const col = pnl === undefined
          ? C.meta
          : isWin
            ? C.green
            : isDirCorrect
              ? C.amber
              : C.red;
        const pStr  = pnl !== undefined ? `${isWin ? "+" : "-"}$${Math.abs(pnl).toFixed(0)}` : "—";
        const time  = t.proceedBar?.t
          ? new Date(t.proceedBar.t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })
          : "—";
        const isActive = i === activeIdx;
        const signal   = t.proceedBar?.signal || t.proceedBar?.trend || "";
        const isBuy    = ["BUY", "LONG", "PROCEED"].includes(String(signal).toUpperCase());
        const isSell   = ["SELL", "SHORT"].includes(String(signal).toUpperCase());
        const dirColor = isBuy ? C.green : isSell ? C.red : C.meta;

        return (
          <button
            key={i}
            onClick={() => onSelect(i)}
            style={{
              background: isActive ? `${col}18` : C.bg,
              border: `1px solid ${isActive ? col : C.border}`,
              borderRadius: 8,
              padding: "8px 14px",
              cursor: "pointer",
              transition: "all .2s ease",
              transform: isActive ? "translateY(-2px)" : "none",
              boxShadow: isActive ? `0 4px 16px ${col}30` : "none",
              minWidth: 80,
              textAlign: "left",
            }}
          >
            <div style={{
              fontFamily: "'DM Mono',monospace", fontSize: 13,
              color: col, fontWeight: 500, lineHeight: 1,
              display: "flex", alignItems: "center", gap: 4,
            }}>
              {pStr}
              {/* amber warning marker for directionally-correct losses */}
              {isDirCorrect && !isWin && (
                <span style={{ fontSize: 9, color: C.amber }} title="Bot called direction correctly — options exit cost you">⚠</span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4 }}>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta }}>
                {time}
              </span>
              {(isBuy || isSell) && (
                <span style={{
                  fontFamily: "'DM Mono',monospace", fontSize: 7,
                  color: dirColor, letterSpacing: ".4px",
                  border: `1px solid ${dirColor}40`, borderRadius: 2,
                  padding: "0 3px",
                }}>
                  {isBuy ? "B" : "S"}
                </span>
              )}
              {t.adjustments?.length > 0 && (
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 7, color: C.amber }}>
                  ⟳{t.adjustments.length}
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// ZONE 3 — TRADE CARD (static 3-act, no in-card animation)
// ============================================================================
function TradeCard({ trade, cardIndex, totalCards, onEvalSelect }) {
  const { proceedBar, reactiveBars, adjustments, ledgerPos } = trade;

  // ── static entry data ────────────────────────────────────────────────────
  const reasoning  = proceedBar.eval_reasoning || proceedBar.eval_narrative_summary || "—";
  const confidence = proceedBar.eval_confidence;
  const entryTime  = proceedBar.t
    ? new Date(proceedBar.t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })
    : "—";

  // BUY/SELL direction chip
  const signal    = proceedBar.signal || proceedBar.trend || proceedBar.eval_decision || "";
  const isBuy     = ["BUY", "LONG", "PROCEED"].includes(String(signal).toUpperCase());
  const isSell    = ["SELL", "SHORT"].includes(String(signal).toUpperCase());
  const dirLabel  = isBuy ? "BUY" : isSell ? "SELL" : null;
  const dirColor  = isBuy ? C.green : C.red;

  // ── ledger data (authoritative) ──────────────────────────────────────────
  const equityPnl   = trade.ledgerPos ? trade.ledgerPos.total_pnl_num : undefined;
  const optionsPnl  = trade.optionsPos ? trade.optionsPos.total_pnl_num : undefined;
  const pnl         = trade.combinedPnl;
  const holdSec     = ledgerPos?.hold_time_seconds;
  const holdStr     = holdSec != null ? (holdSec < 60 ? `${holdSec}s` : `${Math.round(holdSec / 60)}m`) : null;
  const isClosed    = ledgerPos ? ledgerPos.status === "closed" : false;
  const isWin       = pnl !== undefined ? pnl > 0 : false;
  const isDirCorrect = trade.isDirectionallyCorrect;
  // amber = direction right but net loss; green = net win; red = direction wrong loss
  const pnlColor  = pnl === undefined ? C.meta : isWin ? C.green : isDirCorrect ? C.amber : C.red;
  const pnlStr    = pnl !== undefined ? `${isWin ? "+" : "-"}$${Math.abs(pnl).toFixed(0)}` : "—";
  const confStr   = confidence != null ? `${Math.round(confidence * 100)}% conf` : null;
  const exitPrice = ledgerPos?.exit_price ? `@ $${parseFloat(ledgerPos.exit_price).toFixed(2)}` : null;
  const exitTime  = ledgerPos?.exit_filled_at || "—";

  // ── plain-English loss reason ─────────────────────────────────────────────
  const lossReason = (() => {
    if (isWin || pnl === undefined) return null;
    const exitType = (ledgerPos?.exit_order_type || "").toLowerCase();
    if (isDirCorrect) {
      return "Bot called direction correctly — options exit timing created net loss";
    }
    if (exitType.includes("stop")) return "Price moved against position — stop triggered as designed";
    if (exitType.includes("market")) return "Price moved against position — closed at market to limit loss";
    return "Price moved against position before target reached";
  })();

  // ── act 3 ────────────────────────────────────────────────────────────────
  const act3Label = isClosed
    ? isWin
      ? "TARGET HIT"
      : isDirCorrect
        ? "OPTIONS LOSS"
        : "STOP HIT"
    : "OPEN";
  const act3Color  = pnlColor;
  const act3Detail = isClosed
    ? `${pnlStr}${holdStr ? ` · ${holdStr} hold` : ""}${exitPrice ? ` · closed ${exitPrice}` : ""}`
    : "No exit fill recorded";

  // ── BRD-2: regime context for this specific PROCEED bar ─────────────────
  const rc = extractRcFromBar(proceedBar);

  // ── row layout ────────────────────────────────────────────────────────────
  const Row = ({ time, icon, iconColor, label, labelColor, children, noBorder, tinted }) => (
    <div style={{
      display: "grid", gridTemplateColumns: "72px 16px 108px 1fr 28px",
      gap: 10, padding: "14px 20px", alignItems: "flex-start",
      borderBottom: noBorder ? "none" : `1px solid ${C.bg3}`,
      background: tinted ? `${iconColor}08` : "transparent",
    }}>
      <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta, paddingTop: 2, letterSpacing: ".3px" }}>
        {time}
      </span>
      <span style={{ fontSize: 10, color: iconColor, paddingTop: 2 }}>{icon}</span>
      <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: labelColor || iconColor, fontWeight: 600, letterSpacing: ".7px", paddingTop: 2 }}>
        {label}
      </span>
      <div style={{ minWidth: 0 }}>{children}</div>
      <div /> {/* spacer for eval button alignment */}
    </div>
  );

  // Per-reactive-eval "open modal" button inline
  const EvalBtn = ({ bar }) => (
    <button
      onClick={() => onEvalSelect && onEvalSelect(bar)}
      title="Open full evaluation detail"
      style={{
        background: "transparent", border: `1px solid ${C.border}`,
        color: C.meta, borderRadius: 3, padding: "2px 6px",
        fontFamily: "'DM Mono',monospace", fontSize: 8,
        cursor: "pointer", transition: "all .15s", whiteSpace: "nowrap",
        lineHeight: 1.4,
      }}
      onMouseEnter={e => { e.currentTarget.style.color = C.cyan; e.currentTarget.style.borderColor = C.cyan; }}
      onMouseLeave={e => { e.currentTarget.style.color = C.meta; e.currentTarget.style.borderColor = C.border; }}
    >
      ›
    </button>
  );

  return (
    <div
      className="trade-card-enter"
      style={{
        background: C.bg1, border: `1px solid ${C.border}`,
        borderRadius: 14, overflow: "hidden",
        borderLeft: `2px solid ${pnlColor}60`,
      }}
    >
      {/* ── Card header ──────────────────────────────────────────────── */}
      <div style={{
        padding: "12px 20px", borderBottom: `1px solid ${C.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        background: C.bg,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Combined net P&L — the only number that matters */}
          <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600, fontSize: 15, color: pnlColor }}>
            {pnlStr}
          </span>
          {/* Amber marker for directionally-correct losses */}
          {isDirCorrect && !isWin && (
            <span style={{
              fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.amber,
              border: `1px solid ${C.amber}40`, borderRadius: 3, padding: "2px 6px",
              letterSpacing: ".4px",
            }}>
              DIR CORRECT
            </span>
          )}
          {/* Equity + Options breakdown — only when both exist */}
          {equityPnl !== undefined && optionsPnl !== undefined && (
            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta }}>
              equity {equityPnl >= 0 ? "+" : ""}${equityPnl.toFixed(0)} · options {optionsPnl >= 0 ? "+" : ""}${optionsPnl.toFixed(0)}
            </span>
          )}
          {holdStr && (
            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta }}>
              {holdStr} hold
            </span>
          )}
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta }}>
            {entryTime}
          </span>
          {dirLabel && (
            <Chip color={dirColor}>{dirLabel}</Chip>
          )}
          {confStr && (
            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.purple }}>
              {confStr}
            </span>
          )}
          {adjustments.length > 0 && (
            <Chip color={C.amber}>ADJ {adjustments.length}×</Chip>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta }}>
            {cardIndex + 1} / {totalCards}
          </span>
          {/* Open PROCEED eval in modal */}
          <button
            onClick={() => onEvalSelect && onEvalSelect(proceedBar)}
            style={{
              background: "transparent", border: `1px solid ${C.border}`,
              color: C.meta, borderRadius: 4, padding: "4px 10px",
              fontFamily: "'DM Mono',monospace", fontSize: 8, letterSpacing: ".4px",
              cursor: "pointer", transition: "all .15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.color = C.cyan; e.currentTarget.style.borderColor = C.cyan; }}
            onMouseLeave={e => { e.currentTarget.style.color = C.meta; e.currentTarget.style.borderColor = C.border; }}
          >
            FULL EVAL ›
          </button>
        </div>
      </div>

      {/* ── Loss reason banner — only on losses ──────────────────────── */}
      {lossReason && (
        <div style={{
          padding: "8px 20px",
          background: isDirCorrect ? `${C.amber}08` : `${C.red}08`,
          borderBottom: `1px solid ${isDirCorrect ? C.amber : C.red}20`,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 9, color: isDirCorrect ? C.amber : C.red, flexShrink: 0 }}>
            {isDirCorrect ? "⚠" : "⊘"}
          </span>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.body, fontWeight: 300 }}>
            {lossReason}
          </span>
        </div>
      )}

      {/* ── Act 1 — PROCEED ──────────────────────────────────────────── */}
      <Row time={entryTime} icon="▶" iconColor={C.cyan} label="PROCEED">
        {/* BRD-2 chips */}
        {(rc?.dayType || rc?.entryType || rc?.eventDay || rc?.priorProceedTaken) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
            {rc.dayType && (
              <Chip color={dayTypeColor(rc.dayType)}>{rc.dayType.replace(/_/g, " ")}</Chip>
            )}
            {rc.entryType && rc.entryType !== "STANDARD" && (
              <Chip color={rc.entryType === "EARLY_ENTRY" ? C.amber : C.cyan}>
                {rc.entryType.replace(/_/g, " ")}
              </Chip>
            )}
            {rc.eventDay && <Chip color={C.cyan}>EVENT DAY</Chip>}
            {rc.priorProceedTaken && <Chip color={C.amber}>CAPITAL COMMITTED</Chip>}
          </div>
        )}
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.body, lineHeight: 1.65, fontWeight: 300 }}>
          {reasoning.length > 160 ? reasoning.slice(0, 160) + "…" : reasoning}
        </span>
      </Row>

      {/* ── Act 2 — reactive evals static list ───────────────────────── */}
      {reactiveBars.length > 0 ? (
        reactiveBars.map((rb, ri) => {
          const dec      = rb.eval_decision || rb.decision || "HOLD";
          const isAdj    = dec === "ADJUST";
          const rbColor  = isAdj ? C.amber : C.cyan;
          const rbTime   = rb.t
            ? new Date(rb.t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })
            : "—";

          // Exit risk
          const er         = rb.eval_agent_story?.exit_risk || rb.eval_exit_risk;
          const riskLevel  = er?.risk_level;
          const riskColor  = riskLevel === "LOW" ? C.green : riskLevel === "MED" ? C.amber : riskLevel === "HIGH" ? C.red : null;
          const profitLeft = er?.profit_left;

          // One-liner
          const rbReasoning = rb.eval_narrative_summary || rb.eval_reasoning || "";
          let oneLiner;
          if (isAdj) {
            const parts = [];
            if (rb.eval_new_stop_loss) parts.push(`stop → ${parseFloat(rb.eval_new_stop_loss).toFixed(2)}`);
            if (rb.eval_new_target)    parts.push(`target → ${parseFloat(rb.eval_new_target).toFixed(2)}`);
            const adjLine = parts.length > 0 ? parts.join(" · ") : "stop/target updated";
            const snippet = rbReasoning.length > 80 ? rbReasoning.slice(0, 80) + "…" : rbReasoning;
            oneLiner = snippet ? `${adjLine} · ${snippet}` : adjLine;
          } else {
            oneLiner = er?.reason || (rbReasoning.length > 100 ? rbReasoning.slice(0, 100) + "…" : rbReasoning) || "Conditions unchanged";
          }

          // Changed mind
          const thinking   = rb.eval_agent_story?.thinking;
          const changeNote = thinking?.changed_mind
            ? (thinking.decision_evolution?.[0]
                ? `↩ ${thinking.decision_evolution[0].from} → ${thinking.decision_evolution[0].to}${thinking.decision_evolution[0].why ? " · " + thinking.decision_evolution[0].why : ""}`
                : `↩ reconsidered · ${thinking.change_type || "stayed in"}`)
            : null;

          return (
            <div
              key={ri}
              style={{
                display: "grid", gridTemplateColumns: "72px 16px 108px 1fr 28px",
                gap: 10, padding: "11px 20px", alignItems: "flex-start",
                borderBottom: ri < reactiveBars.length - 1 ? `1px solid ${C.bg3}` : `1px solid ${C.bg3}`,
                background: isAdj ? `${C.amber}06` : "transparent",
              }}
            >
              {/* time */}
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta, paddingTop: 2 }}>
                {rbTime}
              </span>
              {/* icon */}
              <span style={{ fontSize: 10, color: rbColor, paddingTop: 2 }}>◈</span>
              {/* label + risk pill */}
              <div style={{ paddingTop: 1 }}>
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: rbColor, fontWeight: 600, letterSpacing: ".7px" }}>
                  {dec}
                </span>
                {riskColor && (
                  <span style={{
                    marginLeft: 6, fontFamily: "'DM Mono',monospace", fontSize: 7,
                    color: riskColor, border: `1px solid ${riskColor}50`,
                    borderRadius: 3, padding: "1px 4px", letterSpacing: ".3px",
                  }}>
                    {riskLevel}{profitLeft ? ` ${profitLeft}` : ""}
                  </span>
                )}
              </div>
              {/* one-liner + change note */}
              <div>
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.body, lineHeight: 1.6, fontWeight: 300 }}>
                  {oneLiner}
                </span>
                {changeNote && (
                  <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.purple, marginTop: 3, fontWeight: 300 }}>
                    {changeNote}
                  </div>
                )}
              </div>
              {/* eval detail button */}
              <div style={{ paddingTop: 2 }}>
                <EvalBtn bar={rb} />
              </div>
            </div>
          );
        })
      ) : (
        <div style={{
          padding: "11px 20px", borderBottom: `1px solid ${C.bg3}`,
          display: "grid", gridTemplateColumns: "72px 16px 108px 1fr",
          gap: 10, alignItems: "center",
        }}>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta }}>—</span>
          <span style={{ fontSize: 10, color: C.meta }}>◈</span>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta, fontWeight: 600, letterSpacing: ".7px" }}>MONITOR</span>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.meta, fontWeight: 300 }}>
            No reactive evaluations recorded
          </span>
        </div>
      )}

      {/* ── Act 3 — CLOSE ────────────────────────────────────────────── */}
      <div style={{
        display: "grid", gridTemplateColumns: "72px 16px 108px 1fr",
        gap: 10, padding: "14px 20px", alignItems: "flex-start",
        background: `${act3Color}08`,
      }}>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta, paddingTop: 2 }}>
          {exitTime}
        </span>
        <span style={{ fontSize: 10, color: act3Color, paddingTop: 2 }}>■</span>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: act3Color, fontWeight: 600, letterSpacing: ".7px", paddingTop: 2 }}>
          {act3Label}
        </span>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.body, lineHeight: 1.65, fontWeight: 300 }}>
          {act3Detail}
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// ZONE 3 — TRADE CAROUSEL (5s auto-advance, icon row nav, no side previews)
// ============================================================================
function TradeCarousel({ trades, onEvalSelect }) {
  const CARD_MS = 5000;
  const [activeIdx, setActiveIdx] = React.useState(0);
  const timerRef = React.useRef(null);
  const [cardKey, setCardKey]     = React.useState(0); // forces fade-in on change

  const goTo = React.useCallback((idx) => {
    clearInterval(timerRef.current);
    setActiveIdx(idx);
    setCardKey(k => k + 1);
    timerRef.current = setInterval(() => {
      setActiveIdx(cur => {
        setCardKey(k => k + 1);
        return (cur + 1) % trades.length;
      });
    }, CARD_MS);
  }, [trades.length]);

  React.useEffect(() => {
    goTo(0);
    return () => clearInterval(timerRef.current);
  }, [trades.length]); // reset when trade list changes

  if (!trades.length) return null;

  return (
    <div>
      {/* Icon nav row */}
      <TradeIconRow trades={trades} activeIdx={activeIdx} onSelect={goTo} />

      {/* Active card — key forces remount → fade-in */}
      <TradeCard
        key={cardKey}
        trade={trades[activeIdx]}
        cardIndex={activeIdx}
        totalCards={trades.length}
        onEvalSelect={onEvalSelect}
      />
    </div>
  );
}

// ============================================================================
// ZONE 3 — TRADE STORY VIEW (builds filled vs unfilled trade lists)
// ============================================================================
function TradeStoryView({ liveBars, ledgerEquityPositions, ledgerOptionsPositions, onEvalSelect }) {
  const isProceedBar = b => ["PROCEED", "BUY"].includes(b.eval_decision || b.decision);

  const { filledTrades, unfilledCount } = React.useMemo(() => {
    const proceedBars = liveBars.filter(isProceedBar);
    if (!proceedBars.length) return { filledTrades: [], unfilledCount: 0 };

    const MATCH_WINDOW_MS   = 5 * 60 * 1000; // 5-minute window for PROCEED→equity match
    const OPTIONS_JOIN_MS   = 90 * 1000;      // 90-second window for equity→options join

    // ── Step 1: group equity rows by entry_submitted_at_raw (partial-fill merge) ──
    // Equity-only pool — one row per PROCEED decision after this grouping
    const lotGroups = new Map();
    for (const lp of (ledgerEquityPositions || [])) {
      const key = lp.entry_submitted_at_raw || "";
      if (!key) continue;
      if (!lotGroups.has(key)) {
        lotGroups.set(key, { ...lp, _lots: [lp] });
      } else {
        const g = lotGroups.get(key);
        g._lots.push(lp);
        g.total_pnl_num = (g.total_pnl_num || 0) + (lp.total_pnl_num || 0);
        g.total_pnl     = g.total_pnl_num.toFixed(2);
        g.quantity      = (g.quantity || 0) + (lp.quantity || 0);
        if ((lp.hold_time_seconds || 0) > (g.hold_time_seconds || 0))
          g.hold_time_seconds = lp.hold_time_seconds;
      }
    }
    const groupedEquity = Array.from(lotGroups.values());

    // ── Step 2: greedy closest-match — PROCEED bars → equity groups ────────
    const scored = [];
    proceedBars.forEach((pb, pi) => {
      const pbTime = pb.t ? new Date(pb.t).getTime() : null;
      if (!pbTime) return;
      groupedEquity.forEach((lg, li) => {
        const lpTime = lg.entry_submitted_at_raw
          ? new Date(lg.entry_submitted_at_raw).getTime() : null;
        if (!lpTime) return;
        const delta = Math.abs(pbTime - lpTime);
        if (delta <= MATCH_WINDOW_MS) scored.push({ pi, li, delta });
      });
    });
    scored.sort((a, b) => a.delta - b.delta);
    const usedPi = new Set(), usedLi = new Set();
    const assignment = new Map(); // pi → equityGroup
    for (const { pi, li } of scored) {
      if (usedPi.has(pi) || usedLi.has(li)) continue;
      assignment.set(pi, groupedEquity[li]);
      usedPi.add(pi);
      usedLi.add(li);
    }

    // ── Step 3: for each matched equity row, join the nearest options row ──
    const optionRows = ledgerOptionsPositions || [];

    const allTrades = proceedBars.map((pb, pi) => {
      const pbTime   = pb.t ? new Date(pb.t).getTime() : null;
      const equityPos = assignment.get(pi) || null;

      // Join options by timestamp proximity to the equity entry
      let optionsPos = null;
      if (equityPos && equityPos.entry_submitted_at_raw) {
        const eqTime = new Date(equityPos.entry_submitted_at_raw).getTime();
        const candidates = optionRows.filter(op => {
          const opTime = op.entry_submitted_at_raw
            ? new Date(op.entry_submitted_at_raw).getTime() : null;
          return opTime && Math.abs(opTime - eqTime) <= OPTIONS_JOIN_MS;
        });
        if (candidates.length > 0) {
          candidates.sort((a, b) => {
            const at = new Date(a.entry_submitted_at_raw).getTime();
            const bt = new Date(b.entry_submitted_at_raw).getTime();
            const eqT = new Date(equityPos.entry_submitted_at_raw).getTime();
            return Math.abs(at - eqT) - Math.abs(bt - eqT);
          });
          optionsPos = candidates[0];
        }
      }

      // Combined net P&L — what actually hit the account
      const equityPnl  = equityPos?.total_pnl_num ?? 0;
      const optionsPnl = optionsPos?.total_pnl_num ?? 0;
      const combinedPnl = equityPos ? equityPnl + optionsPnl : undefined;

      // Directionally correct = equity leg won (hit target) but net is still a loss
      const equityWon        = equityPnl > 0;
      const netLoss          = combinedPnl !== undefined && combinedPnl <= 0;
      const isDirectionallyCorrect = equityWon && netLoss;

      const posId = pb.eval_position_id || pb.position_id;
      let reactiveBars;
      if (posId) {
        reactiveBars = liveBars.filter(b =>
          (b.eval_position_id === posId || b.position_id === posId) && b.eval_type === "REACTIVE"
        );
      } else {
        const entryMs = pbTime || 0;
        const exitRaw = equityPos?.exit_filled_at;
        let exitMs    = entryMs + 3600000;
        if (pbTime && exitRaw && exitRaw.length >= 5) {
          const [h, m, s] = exitRaw.split(":").map(Number);
          const d = new Date(pb.t); d.setHours(h, m, s || 0, 0);
          exitMs = d.getTime() + 5000;
        }
        reactiveBars = liveBars.filter(b => {
          if (b.eval_type !== "REACTIVE") return false;
          const bt = b.t ? new Date(b.t).getTime() : null;
          return bt && bt > entryMs && bt <= exitMs;
        });
      }

      const adjustments = reactiveBars.filter(b => (b.eval_decision || b.decision) === "ADJUST");
      return {
        proceedBar: pb, reactiveBars, adjustments, posId,
        ledgerPos: equityPos, optionsPos,
        combinedPnl, isDirectionallyCorrect,
        isFilled: !!equityPos,
      };
    });

    return {
      filledTrades:  allTrades.filter(t => t.isFilled),
      unfilledCount: allTrades.filter(t => !t.isFilled).length,
    };
  }, [liveBars, ledgerEquityPositions, ledgerOptionsPositions]);

  if (!filledTrades.length && unfilledCount === 0) {
    return (
      <div style={{
        padding: "48px", textAlign: "center", color: C.meta,
        fontFamily: "'DM Mono',monospace", fontSize: 11,
        background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 12,
      }}>
        No PROCEED evaluations found for this session.
      </div>
    );
  }

  if (!filledTrades.length) {
    return (
      <div style={{
        padding: "48px", textAlign: "center", color: C.meta,
        fontFamily: "'DM Mono',monospace", fontSize: 11,
        background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 12,
      }}>
        All PROCEED signals expired at limit — no positions filled this session.
      </div>
    );
  }

  return <TradeCarousel trades={filledTrades} onEvalSelect={onEvalSelect} />;
}

// ============================================================================
// ZONE 3 — STORY VIEW WRAPPER (Story / Detail toggle)
// ============================================================================
function StoryViewWrapper({
  liveBars, ledgerPositions, ledgerEquityPositions, ledgerOptionsPositions, positions,
  selectedPosition, onEvalSelect, onPositionSelect,
}) {
  const [viewMode, setViewMode] = React.useState("story");

  const isProceed  = d => ["PROCEED", "BUY"].includes(d);
  const proceeded  = liveBars.filter(b => isProceed(b.eval_decision || b.decision)).length;
  // filledCount = equity rows only — one per trade decision
  const filledCount = (ledgerEquityPositions || []).filter(lp => lp.status === "closed" || lp.status === "active").length;

  // Date from first bar
  const sessionDate = liveBars[0]?.t
    ? new Date(liveBars[0].t).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <div style={{ marginBottom: 32 }}>
      <style>{CAROUSEL_CSS}</style>

      {/* ── Section header ──────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
        <div>
          <Eyebrow>Session Trades</Eyebrow>
          {/* Hard-fact title line */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{
              fontFamily: "'DM Mono',monospace", fontWeight: 600,
              fontSize: "clamp(15px,1.7vw,20px)", color: C.text,
            }}>
              {filledCount > 0 ? `${filledCount} trade${filledCount > 1 ? "s" : ""}` : "No fills"}
            </span>
            {sessionDate && (
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.meta }}>
                {sessionDate}
              </span>
            )}
            <span style={{
              fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta,
              border: `1px solid ${C.border}`, borderRadius: 3, padding: "2px 6px",
              letterSpacing: ".5px",
            }}>
              SPY 0DTE
            </span>
            {proceeded > filledCount && (
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta }}>
                {filledCount} filled · {proceeded - filledCount} expired at limit
              </span>
            )}
          </div>
        </div>

        {/* Story / Detail toggle */}
        <div style={{
          display: "flex", background: C.bg1, border: `1px solid ${C.border}`,
          borderRadius: 6, overflow: "hidden", flexShrink: 0,
        }}>
          {[["story", "◎ Story"], ["detail", "⊞ Detail"]].map(([mode, label]) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              style={{
                padding: "8px 18px", fontFamily: "'DM Mono',monospace",
                fontSize: 9, fontWeight: 500, letterSpacing: ".5px",
                border: "none", cursor: "pointer", transition: "all .15s",
                background: viewMode === mode ? C.cyan : "transparent",
                color:      viewMode === mode ? "#040810" : C.meta,
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Subheading */}
      <div style={{
        fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.meta,
        fontStyle: "italic", fontWeight: 300, marginBottom: 16,
      }}>
        Cards advance every 5s · click any trade above to jump · use Detail view or Position Ledger for full analysis
      </div>

      {viewMode === "story" ? (
        <div>
          <FillRateStrip liveBars={liveBars} ledgerPositions={ledgerEquityPositions} />
          <TradeStoryView
            liveBars={liveBars}
            ledgerEquityPositions={ledgerEquityPositions}
            ledgerOptionsPositions={ledgerOptionsPositions}
            onEvalSelect={onEvalSelect}
          />
        </div>
      ) : (
        <div style={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
          <EvaluationTimeline
            predictions={liveBars}
            onEvaluationSelect={onEvalSelect}
            selectedEvalId={selectedPosition?.id || selectedPosition?.a_id}
            positions={positions}
            onPositionSelect={onPositionSelect}
          />
        </div>
      )}
    </div>
  );
}


// ZONE 4 — ARCHITECTURE IN ACTION
// ============================================================================
function ArchitectureActivityPanel({ liveBars }) {
  const isSkip      = d => ["SKIP", "SKIP_TRADE"].includes(d);
  const isProceed   = d => ["PROCEED", "BUY"].includes(d);

  const proactiveBars = liveBars.filter(b => !b.eval_position_id || b.eval_type === "PROACTIVE");
  const reactiveBars  = liveBars.filter(b => b.eval_type === "REACTIVE");
  const proceeded     = liveBars.filter(b => isProceed(b.eval_decision || b.decision)).length;
  const skipped       = liveBars.filter(b => isSkip(b.eval_decision || b.decision)).length;
  const adjustments   = liveBars.filter(b => b.eval_decision === "ADJUST").length;

  const layers = [
    {
      icon: "◎", color: C.blue, name: "PREDICT",
      count: `${liveBars.length} bars`,
      summary: `${liveBars.length} price bars processed · directional model running every bar`,
    },
    {
      icon: "⊕", color: C.purple, name: "VALIDATE",
      count: `${proactiveBars.length} checks`,
      summary: `RL cross-check on every proactive evaluation · ${skipped} setups blocked before entry`,
    },
    {
      icon: "⬡", color: C.green, name: "DECIDE",
      count: `${proceeded + skipped} setups`,
      summary: `${proceeded} PROCEED · ${skipped} SKIP · agent made final call on each setup`,
    },
    {
      icon: "◈", color: C.amber, name: "MONITOR",
      count: `${reactiveBars.length} evals`,
      summary: `${reactiveBars.length} reactive evaluations · ${adjustments} adjustments · all positions managed`,
    },
    {
      icon: "◑", color: C.blue, name: "LEARN",
      count: "session",
      summary: "Outcomes recorded to memory · pattern recognition updated · moat deepened",
    },
  ];

  return (
    <div style={{
      background: C.bg1, border: `1px solid ${C.border}`,
      borderRadius: 14, overflow: "hidden", marginBottom: 32,
    }}>
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}` }}>
        <Eyebrow>Architecture in Action</Eyebrow>
        <div style={{
          fontFamily: "'DM Mono',monospace", fontWeight: 500,
          fontSize: "clamp(14px,1.6vw,20px)", color: C.text,
        }}>
          Five layers. Here is what each one did today.
        </div>
      </div>

      {layers.map((l, i) => (
        <div key={i} style={{
          display: "grid",
          gridTemplateColumns: "40px 120px 110px 1fr",
          gap: 16, padding: "16px 24px", alignItems: "center",
          borderBottom: i < layers.length - 1 ? `1px solid ${C.bg2}` : "none",
          borderLeft: `2px solid ${l.color}30`,
        }}>
          <span style={{ fontSize: 16, color: l.color }}>{l.icon}</span>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, fontWeight: 500, color: l.color, letterSpacing: ".8px" }}>
            {l.name}
          </span>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.meta }}>
            {l.count}
          </span>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.body, fontWeight: 300 }}>
            {l.summary}
          </span>
        </div>
      ))}
    </div>
  );
}


// ============================================================================
// ZONE 7 — MEMORY INTELLIGENCE PANEL
// Combines: Memory Similarity, Stop/Target Origin, Regime Strip, Slippage
// All data derived from liveBars (eval_agent_story) + ledgerPositions
// ============================================================================

// ── helpers shared across sub-panels ─────────────────────────────────────────
// ============================================================================
// MEMORY INTELLIGENCE — 3 stacked cards (Regime · Memory · Stop/Target)
// No tabs. Shared time axis. All data from liveBars + ledgerPositions.
// ============================================================================

// ── Shared: build session time bounds ─────────────────────────────────────────
function getSessionTimeBounds(liveBars) {
  const sorted = liveBars.filter(b => b.t).map(b => new Date(b.t).getTime()).sort((a, b) => a - b);
  if (!sorted.length) return { sessionStart: 0, sessionEnd: 0, sessionDuration: 1 };
  const sessionStart    = sorted[0];
  const sessionEnd      = sorted[sorted.length - 1];
  const sessionDuration = Math.max(sessionEnd - sessionStart, 1);
  return { sessionStart, sessionEnd, sessionDuration };
}

// ── pct position along session timeline ───────────────────────────────────────
function toPct(ts, sessionStart, sessionDuration) {
  return Math.max(0, Math.min(100, ((ts - sessionStart) / sessionDuration) * 100));
}

// ── Tooltip state helper (inline, no library) ─────────────────────────────────
function useTooltip() {
  const [tip, setTip] = React.useState(null); // { x, y, content }
  const show = (e, content) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTip({ x: rect.left + rect.width / 2, y: rect.top - 8, content });
  };
  const hide = () => setTip(null);
  return { tip, show, hide };
}

// ── Shared Tooltip portal ──────────────────────────────────────────────────────
function Tooltip({ tip }) {
  if (!tip) return null;
  return (
    <div style={{
      position: "fixed",
      left: tip.x, top: tip.y,
      transform: "translate(-50%, -100%)",
      background: "#0d1117", border: `1px solid ${C.border}`,
      borderRadius: 6, padding: "8px 12px",
      fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.body,
      lineHeight: 1.6, whiteSpace: "pre-line",
      zIndex: 9999, pointerEvents: "none",
      maxWidth: 260, boxShadow: "0 4px 20px rgba(0,0,0,.6)",
    }}>
      {tip.content}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARD 1 — Regime Context Timeline
// ─────────────────────────────────────────────────────────────────────────────
function RegimeCard({ liveBars }) {
  const { tip, show, hide } = useTooltip();
  const { sessionStart, sessionDuration } = React.useMemo(
    () => getSessionTimeBounds(liveBars), [liveBars]
  );

  const sorted = React.useMemo(() =>
    liveBars.filter(b => b.t).sort((a, b) => new Date(a.t) - new Date(b.t)),
  [liveBars]);

  if (!sorted.length) return null;

  // Classify regime — BRD-2 day_type first, then old LLM path, then legacy
  const regimeOf = b => {
    // BRD-2: prefer day_type from regime_summary or regime_context
    const rc = extractRcFromBar(b);
    if (rc?.dayType) return rc.dayType.replace(/_/g, " ");
    // Old path: chart_analysis.llm_full_response.regime_analysis
    const ra = b.eval_agent_story?.chart_analysis?.llm_full_response?.regime_analysis?.regime_classification;
    if (ra) return String(ra).toUpperCase().replace(/_/g, " ");
    // Legacy fields
    const raw = b.eval_agent_story?.regime_history || b.regime || "";
    if (raw) return String(raw).toUpperCase().replace(/_/g, " ").slice(0, 20);
    const trend  = (b.trend  || "").toUpperCase();
    const signal = (b.signal || "").toUpperCase();
    if (["BULL", "BULLISH", "UP"].includes(trend))   return "TRENDING UP";
    if (["BEAR", "BEARISH", "DOWN"].includes(trend)) return "TRENDING DOWN";
    if (trend === signal && ["BUY", "SELL"].includes(trend)) return "TRENDING";
    return "UNKNOWN";
  };

  const regimeColor = r => {
    // BRD-2: match against DAY_TYPE_COLOR keys (spaces swapped back to underscores)
    const key = r.replace(/ /g, "_");
    if (DAY_TYPE_COLOR[key]) return DAY_TYPE_COLOR[key];
    // Legacy fallbacks
    if (r.includes("TRENDING") || r.includes("TREND")) return C.green;
    if (r.includes("WEAK"))     return "#88D400";
    if (r.includes("SIDEWAYS")) return C.purple;
    if (r.includes("RANG"))     return C.blue;
    if (r.includes("VOLAT"))    return C.red;
    if (r.includes("MIXED"))    return C.cyan;
    return C.meta;
  };

  // Collapse into segments
  const segments = [];
  sorted.forEach(b => {
    const reg       = regimeOf(b);
    const dec       = b.eval_decision || b.decision || "";
    const isProceed = ["PROCEED", "BUY"].includes(dec);
    const isSkip    = ["SKIP", "SKIP_TRADE"].includes(dec);
    const ts        = new Date(b.t).getTime();
    const last      = segments[segments.length - 1];
    if (!last || last.regime !== reg) {
      segments.push({ regime: reg, bars: [b], proceeds: isProceed ? 1 : 0, skips: isSkip ? 1 : 0, startTs: ts, endTs: ts });
    } else {
      last.bars.push(b);
      last.endTs = ts;
      if (isProceed) last.proceeds++;
      if (isSkip)    last.skips++;
    }
  });

  // Trade dots: PROCEED bars with ledger match
  const proceedBars = sorted.filter(b => ["PROCEED", "BUY"].includes(b.eval_decision || b.decision));
  const skipBars    = sorted.filter(b => ["SKIP", "SKIP_TRADE"].includes(b.eval_decision || b.decision));

  const firstTime = new Date(sorted[0].t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  const lastTime  = new Date(sorted[sorted.length - 1].t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });

  // Unique regimes for legend
  const uniqueRegimes = [...new Set(segments.map(s => s.regime))];

  return (
    <div style={{
      background: C.bg1, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "20px 24px", marginBottom: 12,
    }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{
          fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta,
          letterSpacing: "1.2px", marginBottom: 5,
        }}>
          MARKET REGIME · AGENT ADAPTATION
        </div>
        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, color: C.text, fontWeight: 500, marginBottom: 6 }}>
          How the agent reads and adapts to changing conditions.
        </div>
        <div style={{
          fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.body,
          lineHeight: 1.7, maxWidth: 620, opacity: 0.85,
        }}>
          The agent classifies market conditions dynamically on every evaluation — no fixed indicators
          required. It draws on accumulated knowledge of how this underlying behaves at different times
          of day, days of the week, and points in the expiry cycle. A SIDEWAYS regime doesn't mean the
          agent steps aside — it means the agent recalibrates, adjusting targets and stop parameters
          to match the environment. The decision to proceed, skip, or adjust happens after regime
          is assessed, and is always weighed against the full context of the current session.
        </div>
      </div>
      <div style={{ height: 1, background: C.border, marginBottom: 14, opacity: 0.5 }} />

      {/* Timeline bar */}
      <div style={{ position: "relative", marginBottom: 4 }}>
        {/* Segment background */}
        <div style={{ display: "flex", height: 28, borderRadius: 4, overflow: "hidden", gap: 1 }}>
          {segments.map((seg, i) => {
            const widthPct  = (seg.bars.length / sorted.length) * 100;
            const col       = regimeColor(seg.regime);
            const hasTrades = seg.proceeds > 0;
            const tipText   = [
              seg.regime,
              `${seg.bars.length} bar${seg.bars.length > 1 ? "s" : ""}`,
              seg.proceeds > 0 ? `${seg.proceeds} trade${seg.proceeds > 1 ? "s" : ""} taken` : null,
              seg.skips    > 0 ? `${seg.skips} skipped` : null,
              // BRD-2: pull from first bar in segment that has regime_context fields
              (() => {
                const rc = seg.bars.map(b => extractRcFromBar(b)).find(r => r);
                if (!rc) return null;
                return [
                  rc.volatilityCtx   ? `volatility: ${rc.volatilityCtx.replace(/_/g, " ")}` : null,
                  rc.entryType       ? `entry: ${rc.entryType.replace(/_/g, " ")}` : null,
                  rc.der != null     ? `DER: ${rc.der.toFixed(2)}` : null,
                  rc.ci14 != null    ? `CI-14: ${rc.ci14.toFixed(1)} (${rc.ci14 < 38.2 ? "trending" : rc.ci14 > 61.8 ? "choppy" : "mid"})` : null,
                  rc.entryAllowed === false && rc.entryBlockReason ? `BLOCKED: ${rc.entryBlockReason}` : null,
                ].filter(Boolean).join("\n");
              })(),
              // Legacy: show regime_analysis detail if no BRD-2 data
              (() => {
                const ra = seg.bars.find(b => b.eval_agent_story?.chart_analysis?.llm_full_response?.regime_analysis)
                  ?.eval_agent_story?.chart_analysis?.llm_full_response?.regime_analysis;
                if (!ra) return null;
                return [
                  ra.flip_rate_assessment != null ? `flip rate: ${(ra.flip_rate_assessment * 100).toFixed(0)}%` : null,
                  ra.confidence_threshold_met != null ? `confidence met: ${ra.confidence_threshold_met ? "yes" : "no"}` : null,
                ].filter(Boolean).join(" · ");
              })(),
            ].filter(Boolean).join("\n");
            return (
              <div
                key={i}
                onMouseEnter={e => show(e, tipText)}
                onMouseLeave={hide}
                style={{
                  width: `${widthPct}%`,
                  background: hasTrades ? col : `${col}35`,
                  cursor: "default",
                }}
              />
            );
          })}
        </div>

        {/* PROCEED dots overlaid */}
        {proceedBars.map((b, i) => {
          const ts  = new Date(b.t).getTime();
          const pct = toPct(ts, sessionStart, sessionDuration);
          return (
            <div key={i} style={{
              position: "absolute", top: "50%",
              left: `${pct}%`, transform: "translate(-50%, -50%)",
              width: 8, height: 8, borderRadius: "50%",
              background: "#fff", border: `1.5px solid ${C.bg}`,
              pointerEvents: "none",
            }} />
          );
        })}

        {/* SKIP marks overlaid */}
        {skipBars.map((b, i) => {
          const ts  = new Date(b.t).getTime();
          const pct = toPct(ts, sessionStart, sessionDuration);
          return (
            <div key={i} style={{
              position: "absolute", top: "50%",
              left: `${pct}%`, transform: "translate(-50%, -50%)",
              width: 4, height: 4, borderRadius: "50%",
              background: C.amber, opacity: 0.7,
              pointerEvents: "none",
            }} />
          );
        })}
      </div>

      {/* Time axis */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta }}>{firstTime}</span>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta }}>{lastTime}</span>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {uniqueRegimes.map(reg => {
          const col    = regimeColor(reg);
          const segs   = segments.filter(s => s.regime === reg);
          const bars   = segs.reduce((s, x) => s + x.bars.length, 0);
          const trades = segs.reduce((s, x) => s + x.proceeds, 0);
          const skips  = segs.reduce((s, x) => s + x.skips, 0);
          const detail = [
            `${bars}b`,
            trades > 0 ? `${trades}t` : null,
            skips  > 0 ? `${skips} skipped` : null,
          ].filter(Boolean).join(" · ");
          return (
            <div key={reg} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "4px 10px", background: C.bg,
              border: `1px solid ${col}40`, borderRadius: 5,
            }}>
              <div style={{ width: 7, height: 7, borderRadius: 2, background: col }} />
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: col }}>
                {reg}
              </span>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta }}>
                {detail}
              </span>
            </div>
          );
        })}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff", border: `1px solid ${C.meta}` }} />
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta }}>trade taken</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.amber }} />
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta }}>skipped</span>
        </div>
      </div>
      <Tooltip tip={tip} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARD 2 — Memory Similarity Timeline
// Vertical bars at any eval timestamp that has memory data.
// Supports both schemas: memory_summary (new) and memory (legacy).
// Currently only REACTIVE bars have memory; will expand to PROACTIVE over time.
// ─────────────────────────────────────────────────────────────────────────────

// Helper: extract normalised memory fields from a bar regardless of schema
function extractMemory(b) {
  const story = b.eval_agent_story;
  if (!story) return null;

  // performance lives as a top-level sibling on eval_agent_story in both schemas
  const perf = story.performance || {};

  // New schema: memory_summary + performance
  if (story.memory_summary) {
    const ms = story.memory_summary;
    return {
      similarity:        ms.top_similarity    ?? ms.similarity_score ?? null,
      found_cases:       ms.found_cases        ?? 0,
      recent_cases:      ms.recent_cases       ?? 0,
      historical_cases:  ms.historical_cases   ?? 0,
      note:              ms.note               ?? null,
      confidence_level:  perf.confidence_level ?? null,
      target_hit_rate:   perf.target_hit_rate  ?? null,
      supports_decision: perf.supports_decision ?? null,
      memory_conflict:   false,
    };
  }

  // Legacy schema: memory (but performance is still a top-level sibling)
  if (story.memory) {
    const m = story.memory;
    return {
      similarity:        m.best_match          ?? null,
      found_cases:       m.found_cases          ?? 0,
      recent_cases:      m.recent_cases         ?? 0,
      historical_cases:  m.historical_cases     ?? 0,
      note:              m.summary              ?? null,
      confidence_level:  perf.confidence_level  ?? m.confidence_level ?? null,
      target_hit_rate:   perf.target_hit_rate   ?? m.target_hit_rate  ?? null,
      supports_decision: perf.supports_decision ?? m.supports_decision ?? null,
      memory_conflict:   m.memory_conflict      ?? false,
    };
  }

  return null;
}

function MemoryCard({ liveBars }) {
  const { tip, show, hide } = useTooltip();
  const { sessionStart, sessionDuration } = React.useMemo(
    () => getSessionTimeBounds(liveBars), [liveBars]
  );

  // Any bar with memory data and non-zero similarity — no eval_type restriction.
  // Zeros are excluded: they're default values not yet populated.
  // Currently only REACTIVE bars carry memory; PROACTIVE will be added later.
  const memoryBars = React.useMemo(() =>
    liveBars
      .filter(b => {
        if (!b.t) return false;
        const mem = extractMemory(b);
        if (!mem) return false;
        // Exclude zero similarity — these are unfilled default values
        if (mem.similarity != null && parseFloat(mem.similarity) === 0) return false;
        return true;
      })
      .sort((a, b) => new Date(a.t) - new Date(b.t)),
  [liveBars]);

  // Aggregate for summary strip
  const { avgSim, avgHitPct, totalCases, stmTotal, ltmTotal, conflictCount, supportsCount } = React.useMemo(() => {
    let matches = [], hitRates = [], totalCases = 0, stmTotal = 0, ltmTotal = 0, conflictCount = 0, supportsCount = 0;
    memoryBars.forEach(b => {
      const mem = extractMemory(b);
      const sim = parseFloat(mem.similarity);
      if (mem.similarity != null && sim > 0) matches.push(sim);
      if (mem.target_hit_rate != null && mem.target_hit_rate > 0) hitRates.push(mem.target_hit_rate);
      totalCases  += mem.found_cases       || 0;
      stmTotal    += mem.recent_cases      || 0;
      ltmTotal    += mem.historical_cases  || 0;
      if (mem.memory_conflict) conflictCount++;
      if (mem.supports_decision === true)  supportsCount++;
    });
    const avgSim    = matches.length  ? matches.reduce((s, v) => s + v, 0)  / matches.length  : null;
    const avgHitPct = hitRates.length ? Math.round(hitRates.reduce((s, v) => s + v, 0) / hitRates.length * 100) : null;
    return { avgSim, avgHitPct, totalCases, stmTotal, ltmTotal, conflictCount, supportsCount };
  }, [memoryBars]);

  const sorted = liveBars.filter(b => b.t).sort((a, b) => new Date(a.t) - new Date(b.t));
  if (!sorted.length) return null;

  const firstTime = new Date(sorted[0].t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  const lastTime  = new Date(sorted[sorted.length - 1].t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });

  const CHART_H = 60; // px tall chart area

  return (
    <div style={{
      background: C.bg1, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "20px 24px", marginBottom: 12,
    }}>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta, letterSpacing: "1.2px", marginBottom: 5 }}>
            ADAPTIVE MEMORY · ALL EVALUATIONS
          </div>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, color: C.text, fontWeight: 500, marginBottom: 6 }}>
            What the bot remembers — and what it learned from it.
          </div>
          <div style={{
            fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.body,
            lineHeight: 1.7, maxWidth: 580, opacity: 0.85,
          }}>
            Each evaluation draws on a two-tier memory system. Short-term memory (STM) captures
            recent trade outcomes and abstracts patterns from the last sessions. Long-term memory
            (LTM) consolidates those abstracts over time — not raw price patterns, but synthesised
            concepts of what worked, what failed, and why. Upon every trade close, outcomes are
            automatically written back into both tiers, so the agent improves continuously without
            human intervention. The bars below show how strongly each evaluation resonated with
            past experience — and whether that experience historically led to profitable outcomes.
          </div>
        </div>
        {avgSim != null && (
          <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 24 }}>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 20, color: C.cyan, fontWeight: 500 }}>
              {Math.round(avgSim * 100)}%
            </div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta }}>
              avg memory match
            </div>
          </div>
        )}
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: C.border, marginBottom: 14, opacity: 0.5 }} />

      {/* Chart area */}
      <div style={{ position: "relative", height: CHART_H, marginBottom: 4 }}>
        {/* Y-axis reference lines */}
        {[100, 70, 50].map(pct => (
          <div key={pct} style={{
            position: "absolute", left: 0, right: 0,
            bottom: `${pct}%`, height: 1,
            background: pct === 70 ? `${C.green}25` : `${C.border}50`,
            pointerEvents: "none",
          }}>
            <span style={{
              position: "absolute", right: 0, bottom: 2,
              fontFamily: "'DM Mono',monospace", fontSize: 7, color: C.meta,
            }}>
              {pct}%
            </span>
          </div>
        ))}

        {/* Empty baseline */}
        <div style={{
          position: "absolute", left: 0, right: 0, bottom: 0, height: 2,
          background: C.bg3, borderRadius: 1,
        }} />

        {/* Vertical bars at each eval with memory data */}
        {memoryBars.map((b, i) => {
          const mem       = extractMemory(b);
          const match     = mem.similarity != null ? parseFloat(mem.similarity) : null;
          const pct       = match != null ? Math.round(match * 100) : null;
          const leftPct   = toPct(new Date(b.t).getTime(), sessionStart, sessionDuration);
          const conflict  = mem.memory_conflict;
          const dec       = b.eval_decision || b.decision || "";
          const evalType  = b.eval_type || "";
          const time      = new Date(b.t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });

          // Color by historical profitability in 5-point bands (target_hit_rate 0–1)
          // Each band interpolates between red→amber→green spectrum
          // Fallback to similarity-based color if no hit rate data
          const hitRate  = mem.target_hit_rate != null ? mem.target_hit_rate : null;
          const hitPct   = hitRate != null ? Math.round(hitRate * 100) : null;
          const BAND_COLORS = [
            "#FF1A3C","#FF2E44","#FF4A2A","#FF6B1A","#FF8C00",
            "#FFB340","#FFD000","#E8E000","#BFDE00","#88D400",
            "#44C87A","#00C896","#00D8A0","#00E8AA","#00E896",
            "#00D8CC","#00C8FF","#00AAFF","#4488FF","#9966FF",
          ];
          const barColor = hitPct != null
            ? BAND_COLORS[Math.min(19, Math.floor(hitPct / 5))]
            : pct == null ? C.meta : pct >= 80 ? C.green : pct >= 60 ? C.cyan : C.amber;

          const supportsStr = mem.supports_decision === true
            ? "✓ Memory SUPPORTS decision"
            : mem.supports_decision === false
              ? "⚠ Memory CONTRADICTS decision"
              : null;

          const tipText = [
            `${time}${evalType ? ` · ${evalType}` : ""}`,
            dec ? `Decision: ${dec}` : null,
            pct != null ? `Similarity: ${pct}%` : "No match data",
            hitPct != null ? `Historically profitable: ${hitPct}%` : null,
            mem.found_cases       ? `${mem.found_cases} cases found` : null,
            mem.recent_cases      ? `  STM: ${mem.recent_cases}` : null,
            mem.historical_cases  ? `  LTM: ${mem.historical_cases}` : null,
            mem.confidence_level  ? `Confidence: ${mem.confidence_level}` : null,
            supportsStr,
            conflict ? "⚠ Memory conflict resolved" : null,
            mem.note || null,
          ].filter(Boolean).join("\n");

          return (
            <div
              key={i}
              onMouseEnter={e => show(e, tipText)}
              onMouseLeave={hide}
              style={{
                position: "absolute",
                left: `${leftPct}%`,
                bottom: 0,
                width: 5,
                height: pct != null ? `${pct}%` : "4%",
                minHeight: 4,
                background: barColor,
                borderRadius: "2px 2px 0 0",
                transform: "translateX(-50%)",
                cursor: "crosshair",
                transition: "opacity .15s",
              }}
              onMouseOver={e => e.currentTarget.style.opacity = "0.7"}
              onMouseOut={e => e.currentTarget.style.opacity = "1"}
            />
          );
        })}

        {/* No memory bars message */}
        {memoryBars.length === 0 && (
          <div style={{
            position: "absolute", inset: 0, display: "flex",
            alignItems: "center", justifyContent: "center",
            fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta,
          }}>
            No evaluations with memory data this session
          </div>
        )}
      </div>

      {/* Time axis */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta }}>{firstTime}</span>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta }}>{lastTime}</span>
      </div>

      {/* Color legend — historical profitability spectrum */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 7, color: C.meta, whiteSpace: "nowrap" }}>hist. profitable</span>
        <div style={{ display: "flex", flex: 1, height: 4, borderRadius: 2, overflow: "hidden" }}>
          {["#FF1A3C","#FF2E44","#FF4A2A","#FF6B1A","#FF8C00","#FFB340","#FFD000","#E8E000",
            "#BFDE00","#88D400","#44C87A","#00C896","#00D8A0","#00E8AA","#00E896","#00D8CC",
            "#00C8FF","#00AAFF","#4488FF","#9966FF"].map((c, i) => (
            <div key={i} style={{ flex: 1, background: c }} />
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", width: 60 }}>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 7, color: C.meta }}>0%</span>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 7, color: C.meta }}>50%</span>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 7, color: "#9966FF" }}>100%</span>
        </div>
      </div>

      {/* Summary strip */}
      {memoryBars.length > 0 && (
        <div style={{
          display: "flex", gap: 20, padding: "10px 14px",
          background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, flexWrap: "wrap",
        }}>
          {[
            { label: "EVAL POINTS",      val: memoryBars.length,  color: C.body   },
            { label: "CASES FOUND",      val: totalCases,          color: C.body   },
            { label: "STM",              val: stmTotal,            color: C.blue   },
            { label: "LTM",              val: ltmTotal,            color: C.purple },
            ...(avgHitPct != null ? [{ label: "HIST PROFITABLE", val: `${avgHitPct}%`, color: avgHitPct >= 60 ? C.green : avgHitPct >= 40 ? C.amber : C.red }] : []),
            ...(supportsCount > 0 ? [{ label: "SUPPORTS",  val: `${supportsCount}/${memoryBars.length}`, color: C.green }] : []),
            ...(conflictCount > 0 ? [{ label: "CONFLICTS", val: conflictCount, color: C.amber }] : []),
          ].map(({ label, val, color }) => (
            <div key={label}>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 7, color: C.meta, letterSpacing: ".8px", marginBottom: 2 }}>{label}</div>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, color, fontWeight: 500 }}>{val}</div>
            </div>
          ))}
          <div style={{ marginLeft: "auto", fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta, fontWeight: 300, alignSelf: "center", maxWidth: 240 }}>
            {stmTotal > ltmTotal
              ? "Primarily recent cases — adapting to current regime"
              : ltmTotal > 0
                ? "Drawing on deep historical patterns — long-term moat active"
                : "Memory architecture active"}
          </div>
        </div>
      )}
      <Tooltip tip={tip} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARD 3 — Stop / Target Adjustment Timeline
// Step-function per position. Fields from OrderLedgerBuilder + PositionAdjustments logic.
// ─────────────────────────────────────────────────────────────────────────────
function StopTargetCard({ liveBars, ledgerPositions }) {
  const { tip, show, hide } = useTooltip();
  const { sessionStart, sessionDuration } = React.useMemo(
    () => getSessionTimeBounds(liveBars), [liveBars]
  );

  const sorted = React.useMemo(() =>
    liveBars.filter(b => b.t).sort((a, b) => new Date(a.t) - new Date(b.t)),
  [liveBars]);

  // ── All hooks must be called before any early returns ─────────────────────
  const trades = React.useMemo(() => {
    const proceedBars = liveBars
      .filter(b => ["PROCEED", "BUY"].includes(b.eval_decision || b.decision) && b.t)
      .sort((a, b) => new Date(a.t) - new Date(b.t));

    return proceedBars.map((pb, tradeIdx) => {
    const pbTime = new Date(pb.t).getTime();

    // Initial parameters: directly from PROCEED eval bar (confirmed always present)
    const initTarget = pb.eval_new_target    != null ? parseFloat(pb.eval_new_target)    : null;
    const initStop   = pb.eval_new_stop_loss != null ? parseFloat(pb.eval_new_stop_loss) : null;

    // Match to ledger for exit data (P&L, exit price, exit time)
    const ledgerPos = ledgerPositions?.find(lp => {
      const lpTime = lp.entry_submitted_at_raw
        ? new Date(lp.entry_submitted_at_raw).getTime() : null;
      return lpTime && Math.abs(pbTime - lpTime) < 90000;
    });

    // Exit timestamp
    const exitRaw = ledgerPos?.exit_filled_at; // "HH:MM:SS"
    let exitTs = null;
    if (exitRaw && exitRaw.length >= 5 && pb.t) {
      const [h, m, s] = exitRaw.split(":").map(Number);
      const d = new Date(pb.t);
      d.setHours(h, m, s || 0, 0);
      exitTs = d.getTime();
    }

    const pnl      = ledgerPos?.total_pnl_num ?? null;
    const exitPrice = ledgerPos?.exit_price ? parseFloat(ledgerPos.exit_price) : null;

    // posId for ADJUST matching
    const posId = pb.eval_position_id || pb.position_id;

    // ── Build adjustment steps (mirrors PositionAdjustments.extractAdjustmentHistory) ──
    let lastTarget = initTarget;
    let lastStop   = initStop;
    const steps = [];

    const adjBars = liveBars
      .filter(b => {
        if (b.eval_decision !== "ADJUST" || !b.t) return false;
        const barPosId = b.eval_position_id || b.position_id;
        // Primary: position ID match
        if (posId && barPosId) return barPosId === posId;
        // Fallback: time window between entry and exit
        const bt  = new Date(b.t).getTime();
        const win = exitTs || (pbTime + 3600000);
        return bt > pbTime && bt <= win + 5000;
      })
      .sort((a, b) => new Date(a.t) - new Date(b.t));

    adjBars.forEach(b => {
      // Exact null check from PositionAdjustments.js lines 55-58
      const newTExists = b.eval_new_target    !== undefined && b.eval_new_target    !== null;
      const newSExists = b.eval_new_stop_loss !== undefined && b.eval_new_stop_loss !== null;
      if (!newTExists && !newSExists) return;

      const newT = newTExists ? parseFloat(b.eval_new_target)    : null;
      const newS = newSExists ? parseFloat(b.eval_new_stop_loss) : null;

      const targetChanged = newT !== null && newT !== lastTarget;
      const stopChanged   = newS !== null && newS !== lastStop;
      if (!targetChanged && !stopChanged) return;

      // Chart analysis context
      const chartLLM = b.eval_agent_story?.chart_analysis?.llm_full_response || {};
      const oa       = chartLLM.overall_assessment || {};
      const er       = b.eval_agent_story?.exit_risk || {};

      steps.push({
        ts:          new Date(b.t).getTime(),
        timeStr:     new Date(b.t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }),
        oldTarget:   lastTarget,
        newTarget:   targetChanged ? newT : lastTarget,
        oldStop:     lastStop,
        newStop:     stopChanged   ? newS : lastStop,
        targetChanged,
        stopChanged,
        reason:      b.eval_pattern_influence || b.eval_narrative_summary || b.eval_reasoning || "",
        applied:     b.eval_adjustment_applied || "",
        // chart context
        direction:   oa.dominant_direction || "",
        conflicting: !!oa.conflicting_signals_present,
        confluence:  oa.pattern_confluence_strength != null
                       ? Math.round(oa.pattern_confluence_strength * 100) : null,
        support:     oa.key_support_level  || null,
        resistance:  oa.key_resistance_level || null,
        // exit risk at this moment
        riskLevel:   er.risk_level   || "",
        profitLeft:  er.profit_left  || "",
        riskReason:  er.reason       || "",
      });

      if (targetChanged) lastTarget = newT;
      if (stopChanged)   lastStop   = newS;
    });

    // ── Outcome classification ─────────────────────────────────────────────
    // Compare exit_price to final target/stop in play (±$0.15 tolerance for options)
    const TOL = 0.15;
    let outcome = null;
    if (exitPrice != null && lastTarget != null && lastStop != null) {
      const hadAdj = steps.length > 0;
      if (Math.abs(exitPrice - lastTarget) <= TOL) {
        outcome = hadAdj ? "Adjusted target achieved" : "Original target achieved";
      } else if (Math.abs(exitPrice - lastStop) <= TOL) {
        outcome = hadAdj ? "Adjusted stop hit" : "Original stop hit";
      } else if (ledgerPos?.exit_order_type?.includes("stop")) {
        outcome = "Stop triggered";
      } else if (exitPrice > (lastStop ?? 0)) {
        outcome = "Closed between levels";
      } else {
        outcome = "Closed";
      }
    }

    const isWin    = pnl != null ? pnl > 0 : null;
    const exitColor = isWin == null ? C.meta : isWin ? C.green : C.red;

    return {
      tradeIdx, pb, pbTime, exitTs, exitPrice, exitColor,
      pnl, isWin, outcome,
      initTarget, initStop,
      lastTarget, lastStop,
      steps,
      posId,
      timeStr: new Date(pb.t).toLocaleTimeString("en-US", {
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
      }),
      entryReason: pb.eval_narrative_summary || pb.eval_reasoning || "",
    };
  });
  }, [liveBars, ledgerPositions]);

  // ── Early returns after all hooks ─────────────────────────────────────────
  if (!sorted.length) return null;
  if (!trades.length) return null;

  const firstTime = new Date(sorted[0].t)
    .toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  const lastTime  = new Date(sorted[sorted.length - 1].t)
    .toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });

  // ── Summary stats for footer ──────────────────────────────────────────────
  const totalAdj       = trades.reduce((s, t) => s + t.steps.length, 0);
  const adjTradeCount  = trades.filter(t => t.steps.length > 0).length;
  const outcomes       = trades.map(t => t.outcome).filter(Boolean);
  const targetHits     = outcomes.filter(o => o.includes("target")).length;
  const stopHits       = outcomes.filter(o => o.includes("stop") || o.includes("Stop")).length;

  // ── Render ─────────────────────────────────────────────────────────────────
  const BAR_H  = 28; // height of the base timeline bar in px
  const TICK_H = BAR_H; // ticks span full bar height

  return (
    <div style={{
      background: C.bg1, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "20px 24px", marginBottom: 12,
    }}>
      {/* Header */}
      <div style={{ marginBottom: 14 }}>
        <div style={{
          fontFamily: "'DM Mono',monospace", fontSize: 9,
          color: C.meta, letterSpacing: "1.2px", marginBottom: 5,
        }}>
          STOP / TARGET · AGENT RISK CONTROL
        </div>
        <div style={{
          fontFamily: "'DM Mono',monospace", fontWeight: 500,
          fontSize: 13, color: C.text, marginBottom: 6,
        }}>
          How does the agent know when to hold, adjust, or walk away?
        </div>
        <div style={{
          fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.body,
          lineHeight: 1.7, maxWidth: 620, opacity: 0.85, marginBottom: 12,
        }}>
          At entry, the agent sets a target and stop loss by synthesising predictive models,
          visual chart analysis, and statistical conditions at that exact moment — this is
          the trade thesis. But a thesis can be wrong, or the market can structurally change
          after entry. On every reactive evaluation, the agent stress-tests the original
          thesis against live conditions. If the thesis holds, it does nothing (HOLD). If
          conditions have shifted but the trade is recoverable, it recalculates the target
          and stop to match the new reality (ADJUST). If the original call was simply wrong,
          it closes early to limit losses (CLOSE) — before the stop is even hit. Memory
          plays a role throughout: the agent draws on past outcomes in similar conditions to
          inform whether adjusting or closing has historically been the right call.
        </div>
        {/* Legend */}
        <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          {[
            { color: C.cyan,  shape: "tick", label: "ESTABLISHED" },
            { color: C.amber, shape: "tick", label: "ADJUSTED"    },
            { color: C.green, shape: "dot",  label: "TARGET HIT"  },
            { color: C.red,   shape: "dot",  label: "STOP HIT"    },
          ].map(({ color, shape, label }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              {shape === "tick"
                ? <div style={{ width: 3, height: 12, background: color, borderRadius: 1 }} />
                : <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
              }
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 7, color: C.meta, letterSpacing: ".4px" }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ height: 1, background: C.border, marginBottom: 14, opacity: 0.5 }} />

      {/* ── Single session timeline bar ──────────────────────────────────── */}
      <div style={{ position: "relative", height: BAR_H, marginBottom: 4 }}>

        {/* Base bar — full width, dark neutral */}
        <div style={{
          position: "absolute", inset: 0,
          background: C.bg3, borderRadius: 4,
        }} />

        {/* Per-trade active span — tinted band from entry to exit */}
        {trades.map((t, i) => {
          const leftPct  = toPct(t.pbTime, sessionStart, sessionDuration);
          const rightPct = t.exitTs
            ? toPct(t.exitTs, sessionStart, sessionDuration)
            : 100;
          const widthPct = Math.max(0.5, rightPct - leftPct);
          const spanColor = t.isWin == null ? C.meta : t.isWin ? C.green : C.red;
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                left: `${leftPct}%`,
                width: `${widthPct}%`,
                top: 0, bottom: 0,
                background: `${spanColor}20`,
                borderRadius: 2,
              }}
            />
          );
        })}

        {/* PROCEED ticks — cyan, full bar height */}
        {trades.map((t, i) => {
          const leftPct = toPct(t.pbTime, sessionStart, sessionDuration);
          const tipText = [
            `${t.timeStr}  T${t.tradeIdx + 1} ESTABLISHED`,
            `Target:  $${t.initTarget?.toFixed(2) ?? "—"}`,
            `Stop:    $${t.initStop?.toFixed(2)   ?? "—"}`,
            t.entryReason
              ? "─────────────────\n" + t.entryReason.slice(0, 120)
              : null,
          ].filter(Boolean).join("\n");

          return (
            <div
              key={i}
              onMouseEnter={e => show(e, tipText)}
              onMouseLeave={hide}
              style={{
                position: "absolute",
                left: `${leftPct}%`,
                top: 0, height: TICK_H,
                width: 3,
                transform: "translateX(-50%)",
                background: C.cyan,
                borderRadius: 1,
                cursor: "crosshair",
                zIndex: 2,
              }}
            />
          );
        })}

        {/* ADJUST ticks — amber, full bar height */}
        {trades.flatMap((t, ti) =>
          t.steps.map((s, si) => {
            const leftPct = toPct(s.ts, sessionStart, sessionDuration);

            const dirColor = s.direction.includes("bull") ? C.green
              : s.direction.includes("bear") ? C.red : C.meta;

            const tipLines = [
              `${s.timeStr}  T${t.tradeIdx + 1} ADJUSTED`,
              s.targetChanged
                ? `Target:  $${s.oldTarget?.toFixed(2) ?? "?"} → $${s.newTarget?.toFixed(2) ?? "?"}`
                : `Target:  $${s.newTarget?.toFixed(2) ?? "—"}  unchanged`,
              s.stopChanged
                ? `Stop:    $${s.oldStop?.toFixed(2)   ?? "?"} → $${s.newStop?.toFixed(2)   ?? "?"}`
                : `Stop:    $${s.newStop?.toFixed(2)   ?? "—"}  unchanged`,
              s.reason   ? "─────────────────\n" + s.reason.slice(0, 120) : null,
              s.direction
                ? `Chart:   ${s.direction}${s.confluence != null ? ` · confluence ${s.confluence}%` : ""}${s.conflicting ? " · ⚠ conflicting signals" : ""}`
                : null,
              (s.support || s.resistance)
                ? `Levels:  ${s.support ? `S $${s.support}` : ""} ${s.resistance ? `R $${s.resistance}` : ""}`.trim()
                : null,
              s.riskLevel
                ? `Risk:    ${s.riskLevel}${s.profitLeft ? ` · ${s.profitLeft} profit remaining` : ""}${s.riskReason ? ` · ${s.riskReason}` : ""}`
                : null,
              s.applied  ? `Applied: ${s.applied}` : null,
            ].filter(Boolean).join("\n");

            return (
              <div
                key={`${ti}-${si}`}
                onMouseEnter={e => show(e, tipLines)}
                onMouseLeave={hide}
                style={{
                  position: "absolute",
                  left: `${leftPct}%`,
                  top: 0, height: TICK_H,
                  width: 3,
                  transform: "translateX(-50%)",
                  background: C.amber,
                  borderRadius: 1,
                  cursor: "crosshair",
                  zIndex: 3,
                }}
              />
            );
          })
        )}

        {/* Exit dots */}
        {trades.map((t, i) => {
          if (!t.exitTs) return null;
          const leftPct = toPct(t.exitTs, sessionStart, sessionDuration);
          const pnlStr  = t.pnl != null
            ? `${t.pnl > 0 ? "+" : ""}$${Math.abs(t.pnl).toFixed(0)}`
            : "—";
          const tipText = [
            `${new Date(t.exitTs).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}  T${t.tradeIdx + 1} CLOSED`,
            t.exitPrice != null ? `Exit:    $${t.exitPrice.toFixed(2)}` : null,
            t.outcome   ? `Outcome: ${t.outcome}` : null,
            `P&L:     ${pnlStr}`,
          ].filter(Boolean).join("\n");

          return (
            <div
              key={i}
              onMouseEnter={e => show(e, tipText)}
              onMouseLeave={hide}
              style={{
                position: "absolute",
                left: `${leftPct}%`,
                top: "50%",
                transform: "translate(-50%, -50%)",
                width: 10, height: 10,
                borderRadius: "50%",
                background: t.exitColor,
                border: `2px solid ${C.bg}`,
                cursor: "crosshair",
                zIndex: 4,
              }}
            />
          );
        })}
      </div>

      {/* Time axis */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta }}>{firstTime}</span>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta }}>{lastTime}</span>
      </div>

      {/* Trade label row — small pills below the bar at each entry position */}
      <div style={{ position: "relative", height: 20, marginBottom: 8 }}>
        {trades.map((t, i) => {
          const leftPct = toPct(t.pbTime, sessionStart, sessionDuration);
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                left: `${leftPct}%`,
                transform: "translateX(-50%)",
                fontFamily: "'DM Mono',monospace",
                fontSize: 8,
                color: C.cyan,
                whiteSpace: "nowrap",
              }}
            >
              T{t.tradeIdx + 1}
            </div>
          );
        })}
      </div>

      {/* Summary line */}
      <div style={{
        fontFamily: "'DM Mono',monospace", fontSize: 9,
        color: C.meta, fontWeight: 300,
      }}>
        {[
          `${trades.length} trade${trades.length > 1 ? "s" : ""}`,
          totalAdj > 0
            ? `${totalAdj} adjustment${totalAdj > 1 ? "s" : ""} across ${adjTradeCount} trade${adjTradeCount > 1 ? "s" : ""}`
            : "no adjustments — held as planned",
          targetHits > 0 ? `${targetHits} target hit` : null,
          stopHits   > 0 ? `${stopHits} stop hit`     : null,
        ].filter(Boolean).join(" · ")}
        {totalAdj > 0 && (
          <span style={{ color: `${C.meta}80` }}> · hover ticks for detail</span>
        )}
      </div>

      <Tooltip tip={tip} />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// CARD 4 — Options Strategy Timeline
// Segment bar colored by option_strategy per proactive eval.
// Hedge bias shown as tint overlay. Hover shows options_notes.
// ─────────────────────────────────────────────────────────────────────────────
function OptionsStrategyCard({ liveBars }) {
  const { tip, show, hide } = useTooltip();
  const { sessionStart, sessionDuration } = React.useMemo(
    () => getSessionTimeBounds(liveBars), [liveBars]
  );

  const STRATEGY_COLOR = {
    "LONG_PREMIUM":  C.cyan,
    "DEBIT_SPREAD":  C.green,
    "SKIP_OPTIONS":  C.meta,
  };
  const HEDGE_LABEL = {
    "NONE":                  null,
    "REDUCE_SIZE":           "reduce size",
    "AIRBAG_OPPOSITE_LONG":  "airbag hedge",
    "CONVERT_TO_SPREAD":     "convert to spread",
  };
  const HEDGE_TINT = {
    "NONE":                  null,
    "REDUCE_SIZE":           C.amber,
    "AIRBAG_OPPOSITE_LONG":  C.red,
    "CONVERT_TO_SPREAD":     C.purple,
  };

  // Proactive bars with chart_analysis — no eval_type filter
  const optionBars = React.useMemo(() =>
    liveBars
      .filter(b => b.t && b.eval_agent_story?.chart_analysis?.llm_full_response?.option_strategy)
      .sort((a, b) => new Date(a.t) - new Date(b.t)),
  [liveBars]);

  // Collapse into strategy segments
  const segments = React.useMemo(() => {
    const segs = [];
    optionBars.forEach(b => {
      const llm      = b.eval_agent_story.chart_analysis.llm_full_response;
      const strategy = (llm.option_strategy || "SKIP_OPTIONS").toUpperCase();
      const hedge    = (llm.hedge_bias      || "NONE").toUpperCase();
      const hedgePct = llm.hedge_size_pct   || 0;
      const notes    = llm.options_notes    || null;
      const dec      = b.eval_decision || b.decision || "";
      const ts       = new Date(b.t).getTime();
      const last     = segs[segs.length - 1];
      if (!last || last.strategy !== strategy || last.hedge !== hedge) {
        segs.push({ strategy, hedge, hedgePct, notes, bars: [b], startTs: ts, endTs: ts, dec });
      } else {
        last.bars.push(b);
        last.endTs = ts;
        last.notes = notes || last.notes; // keep most recent non-null note
      }
    });
    return segs;
  }, [optionBars]);

  // Summary stats
  const { stratCounts, avgHedgePct } = React.useMemo(() => {
    const counts = {};
    let hedgeSum = 0, hedgeN = 0;
    optionBars.forEach(b => {
      const llm = b.eval_agent_story.chart_analysis.llm_full_response;
      const s = (llm.option_strategy || "SKIP_OPTIONS").toUpperCase();
      counts[s] = (counts[s] || 0) + 1;
      if (llm.hedge_size_pct > 0) { hedgeSum += llm.hedge_size_pct; hedgeN++; }
    });
    return { stratCounts: counts, avgHedgePct: hedgeN ? Math.round(hedgeSum / hedgeN * 100) : 0 };
  }, [optionBars]);

  const uniqueStrategies = [...new Set(segments.map(s => s.strategy))];

  if (!optionBars.length) return null;

  const sorted = liveBars.filter(b => b.t).sort((a, b) => new Date(a.t) - new Date(b.t));
  const firstTime = new Date(sorted[0].t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  const lastTime  = new Date(sorted[sorted.length - 1].t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });

  const BAR_H = 28;

  return (
    <div style={{
      background: C.bg1, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "20px 24px", marginBottom: 12,
    }}>
      {/* Header */}
      <div style={{ marginBottom: 14 }}>
        <div style={{
          fontFamily: "'DM Mono',monospace", fontSize: 9,
          color: C.meta, letterSpacing: "1.2px", marginBottom: 5,
        }}>
          OPTIONS STRATEGY · AGENT SELECTION
        </div>
        <div style={{
          fontFamily: "'DM Mono',monospace", fontWeight: 500,
          fontSize: 13, color: C.text, marginBottom: 6,
        }}>
          How does the agent turn a price target into an options position?
        </div>
        <div style={{
          fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.body,
          lineHeight: 1.7, maxWidth: 620, opacity: 0.85, marginBottom: 12,
        }}>
          The agent tracks the underlying asset price — not the option price — against the
          target and stop loss set at entry. When the asset hits either level, the option
          is closed at market immediately. Strike selection and limit pricing are determined
          at entry based on maximum dollar ROI for the session's expiry constraints, using
          live ask/bid data. The option strategy itself — whether to buy premium outright
          or use a spread — is chosen based on regime, volatility, and confidence level at
          that exact moment. You don't need to monitor Greeks or the option price.
          The underlying asset price is the only trigger that matters.
        </div>
        <div style={{ height: 1, background: C.border, marginBottom: 12, opacity: 0.5 }} />

        {/* Legend */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          {[
            { key: "LONG_PREMIUM", label: "LONG PREMIUM" },
            { key: "DEBIT_SPREAD", label: "DEBIT SPREAD" },
            { key: "SKIP_OPTIONS", label: "SKIP OPTIONS" },
          ].filter(({ key }) => stratCounts[key] > 0).map(({ key, label }) => (
            <div key={key} style={{
              display: "flex", alignItems: "center", gap: 5,
              padding: "3px 8px", background: C.bg,
              border: `1px solid ${STRATEGY_COLOR[key]}40`, borderRadius: 4,
            }}>
              <div style={{ width: 7, height: 7, borderRadius: 2, background: STRATEGY_COLOR[key] }} />
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: STRATEGY_COLOR[key] }}>{label}</span>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta }}>{stratCounts[key]}x</span>
            </div>
          ))}
          {avgHedgePct > 0 && (
            <div style={{
              display: "flex", alignItems: "center", gap: 5,
              padding: "3px 8px", background: C.bg,
              border: `1px solid ${C.amber}40`, borderRadius: 4,
            }}>
              <div style={{ width: 7, height: 7, borderRadius: 2, background: C.amber, opacity: 0.6 }} />
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.amber }}>HEDGE ACTIVE</span>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta }}>avg {avgHedgePct}%</span>
            </div>
          )}
        </div>
      </div>

      {/* Timeline bar */}
      <div style={{ position: "relative", marginBottom: 4 }}>

        {/* Gray baseline — full session width */}
        <div style={{
          position: "absolute", inset: 0,
          background: C.bg3, borderRadius: 4, height: BAR_H,
        }} />

        {/* Strategy ticks — absolute positioned at each eval timestamp */}
        <div style={{ position: "relative", height: BAR_H }}>
          {optionBars.map((b, i) => {
            const llm      = b.eval_agent_story.chart_analysis.llm_full_response;
            const strategy = (llm.option_strategy || "SKIP_OPTIONS").toUpperCase();
            const hedge    = (llm.hedge_bias      || "NONE").toUpperCase();
            const hedgePct = llm.hedge_size_pct   || 0;
            const notes    = llm.options_notes    || null;
            const col      = STRATEGY_COLOR[strategy] || C.meta;
            const hedgeTint = HEDGE_TINT[hedge];
            const hedgeLabel = HEDGE_LABEL[hedge];
            const isProceed = ["PROCEED","BUY"].includes(b.eval_decision || b.decision);
            const leftPct  = toPct(new Date(b.t).getTime(), sessionStart, sessionDuration);
            const time     = new Date(b.t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });

            const tipText = [
              `${time} · ${strategy.replace(/_/g, " ")}`,
              isProceed ? "Trade taken" : "No trade",
              hedge !== "NONE" ? `Hedge: ${hedgeLabel}${hedgePct > 0 ? ` (${Math.round(hedgePct * 100)}%)` : ""}` : null,
              notes,
            ].filter(Boolean).join("\n");

            return (
              <div
                key={i}
                onMouseEnter={e => show(e, tipText)}
                onMouseLeave={hide}
                style={{
                  position: "absolute",
                  left: `${leftPct}%`,
                  top: 0,
                  transform: "translateX(-50%)",
                  width: 10,
                  height: BAR_H,
                  background: isProceed ? col : `${col}50`,
                  borderRadius: 2,
                  cursor: "crosshair",
                  boxShadow: isProceed ? `0 0 6px ${col}60` : "none",
                }}
              >
                {/* Hedge tint overlay */}
                {hedgeTint && (
                  <div style={{
                    position: "absolute", inset: 0,
                    background: hedgeTint,
                    opacity: 0.4, borderRadius: 2,
                  }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Time axis */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta }}>{firstTime}</span>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta }}>{lastTime}</span>
      </div>

      {/* Summary strip */}
      <div style={{
        display: "flex", gap: 20, padding: "10px 14px",
        background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, flexWrap: "wrap",
      }}>
        {Object.entries(stratCounts).map(([strat, count]) => (
          <div key={strat}>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 7, color: C.meta, letterSpacing: ".8px", marginBottom: 2 }}>
              {strat.replace(/_/g, " ")}
            </div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, color: STRATEGY_COLOR[strat] || C.body, fontWeight: 500 }}>
              {count}x
            </div>
          </div>
        ))}
        {avgHedgePct > 0 && (
          <div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 7, color: C.meta, letterSpacing: ".8px", marginBottom: 2 }}>AVG HEDGE</div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, color: C.amber, fontWeight: 500 }}>{avgHedgePct}%</div>
          </div>
        )}
        <div style={{ marginLeft: "auto", fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta, alignSelf: "center", maxWidth: 260 }}>
          {stratCounts["DEBIT_SPREAD"] > (stratCounts["LONG_PREMIUM"] || 0)
            ? "Spread-dominant session — agent favoured defined risk in lower volatility"
            : stratCounts["LONG_PREMIUM"] > 0
              ? "Premium buying session — agent found sufficient edge to buy outright"
              : "Options skipped — agent assessed premium cost exceeded expected value"}
        </div>
      </div>

      <Tooltip tip={tip} />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// MEMORY INTELLIGENCE PANEL — 3 stacked cards, no tabs
// ─────────────────────────────────────────────────────────────────────────────
function MemoryIntelligencePanel({ liveBars, ledgerPositions }) {
  if (!liveBars?.length) return null;
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ marginBottom: 16 }}>
        <Eyebrow>Agent Intelligence</Eyebrow>
      </div>
      <RegimeCard          liveBars={liveBars} />
      <OptionsStrategyCard liveBars={liveBars} />
      <MemoryCard          liveBars={liveBars} />
      <StopTargetCard      liveBars={liveBars} ledgerPositions={ledgerPositions} />
    </div>
  );
}


// ============================================================================
// ZONE 6 — MEMORY TRAIL
// ============================================================================
function MemoryTrailCard({ liveBars, sessionCount }) {
  const lastProceedBar = liveBars.slice().reverse().find(b =>
    ["PROCEED", "BUY"].includes(b.eval_decision || b.decision) && b.eval_agent_story
  );
  const mem = lastProceedBar?.eval_agent_story?.memory_summary
    || lastProceedBar?.eval_agent_story?.memory
    || {};

  const entering = [
    mem.stm_cases_used
      ? `${mem.stm_cases_used} similar sessions in STM memory`
      : "Short-term memory patterns referenced",
    mem.regime_history
      ? `Historical regime data: ${mem.regime_history}`
      : "Regime classification informed by session history",
    mem.ltm_patterns
      ? `${mem.ltm_patterns} LTM patterns matched this session`
      : "Long-term memory patterns active",
  ];

  const learned = [
    "Closed position outcomes recorded to LTM",
    "Regime classification updated with today's data",
    "Pattern recognition refreshed · moat deepened",
  ];

  return (
    <div style={{
      background: C.bg1, border: `1px solid ${C.border}`,
      borderRadius: 14, padding: "28px 32px", marginBottom: 32,
    }}>
      <Eyebrow>Memory Trail</Eyebrow>
      <div style={{
        fontFamily: "'DM Mono',monospace", fontWeight: 500,
        fontSize: "clamp(14px,1.6vw,20px)", color: C.text, marginBottom: 24,
      }}>
        What the bot remembered. What it learned.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        {/* Entering */}
        <div style={{
          background: C.bg, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: "20px 24px",
          borderTop: `2px solid ${C.blue}`,
        }}>
          <div style={{
            fontFamily: "'DM Mono',monospace", fontSize: 9,
            color: C.blue, letterSpacing: "1.2px", marginBottom: 16,
          }}>
            ENTERING THIS SESSION
          </div>
          {entering.map((item, i) => (
            <div key={i} style={{ display: "flex", gap: 10, marginBottom: 10 }}>
              <span style={{ color: C.blue, fontSize: 10, flexShrink: 0 }}>◑</span>
              <span style={{
                fontFamily: "'DM Mono',monospace", fontSize: 10,
                color: C.body, fontWeight: 300, lineHeight: 1.6,
              }}>
                {item}
              </span>
            </div>
          ))}
        </div>

        {/* Learned */}
        <div style={{
          background: C.bg, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: "20px 24px",
          borderTop: `2px solid ${C.green}`,
        }}>
          <div style={{
            fontFamily: "'DM Mono',monospace", fontSize: 9,
            color: C.green, letterSpacing: "1.2px", marginBottom: 16,
          }}>
            LEARNED FROM THIS SESSION
          </div>
          {learned.map((item, i) => (
            <div key={i} style={{ display: "flex", gap: 10, marginBottom: 10 }}>
              <span style={{ color: C.green, fontSize: 10, flexShrink: 0 }}>◎</span>
              <span style={{
                fontFamily: "'DM Mono',monospace", fontSize: 10,
                color: C.body, fontWeight: 300, lineHeight: 1.6,
              }}>
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Moat statement */}
      <div style={{
        marginTop: 20, padding: "14px 20px",
        background: "rgba(0,232,150,0.04)",
        border: `1px solid ${C.green}18`, borderRadius: 8,
      }}>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.body, fontWeight: 300 }}>
          {sessionCount > 0
            ? `This is session ${sessionCount} of record. The system has processed hundreds of trade outcomes. Pattern recognition improves with every closed position — the moat widens with time.`
            : "Memory architecture active. Every session adds to the system's pattern recognition. The moat widens with time."}
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// API
// ============================================================================
const APIUrl = config.API_BASE_URL;

function getDataAPI(ts, resource) {
  return axios
    .post(
      APIUrl + "/" + resource,
      { startDate: ts, username: "admin", days: 30 },
      { headers: { "x-api-key": config.API_KEY, authorizationToken: getToken() } }
    )
    .then(response => {
      console.log(`Loaded ${resource}:`, response.data.length, "items");
      return response;
    })
    .catch(error => {
      console.error("Error loading data:", error);
      throw error;
    });
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const DailyDetailDashboard = () => {
  // -------------------------------------------------------------------------
  // React Router — read location state from TradeDashboard navigation
  // -------------------------------------------------------------------------
  const location = useLocation();
  const history  = useHistory();
  const sessionSummary = location.state?.sessionSummary || null;

  // -------------------------------------------------------------------------
  // URL param
  // -------------------------------------------------------------------------
  const urlParams = new URLSearchParams(window.location.search);
  const dateParam = urlParams.get("date");

  // -------------------------------------------------------------------------
  // RAW STATE — unchanged from original
  // -------------------------------------------------------------------------
  const [liveBars, setLiveBars]   = useState([]);
  const [cleanbars, setCleanbars] = useState([]);
  const [bars, setBars]           = useState([]);
  const [rawOrders, setRawOrders] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(0);

  // -------------------------------------------------------------------------
  // UI STATE — unchanged
  // -------------------------------------------------------------------------
  const [selectedPosition, setSelectedPosition]     = useState(null);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [showEvalModal, setShowEvalModal]             = useState(false);
  const [showPositionModal, setShowPositionModal]     = useState(false);
  const [loading, setLoading]                         = useState(true);
  const [error, setError]                             = useState(null);

  const [dateInfo, setDateInfo] = useState({ date: dateParam, displayDate: "", symbol: "" });

  const dispatch = useDispatch();

  // -------------------------------------------------------------------------
  // DATA LOADING — unchanged
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!dateParam) {
      setError("No date specified");
      setLoading(false);
      return;
    }

    const loadHistoricalData = async () => {
      setLoading(true);
      setError(null);

      dispatch(clearPrediction());
      dispatch(clearOrder());
      dispatch(clearActivity());

      try {
        console.log("Loading historical data for:", dateParam);

        const predResponse = await getDataAPI(dateParam, "predictions");
        const rawHistory   = predResponse.data;

        let outputArr = rawHistory.map(rawBar => ({
          ...prepareData(rawBar),
          eval_decision:         rawBar.eval_decision,
          eval_type:             rawBar.eval_type,
          eval_reasoning:        rawBar.eval_reasoning,
          eval_narrative_summary:rawBar.eval_narrative_summary,
          eval_agent_story:      rawBar.eval_agent_story,
          eval_position_id:      rawBar.eval_position_id,
          eval_new_stop_loss:    rawBar.eval_new_stop_loss,
          eval_new_target:       rawBar.eval_new_target,
          eval_pattern_influence:rawBar.eval_pattern_influence,
          eval_adjustment_applied:rawBar.eval_adjustment_applied,
          eval_execution_time:   rawBar.eval_execution_time,
          eval_error:            rawBar.eval_error,
          eval_confidence:       rawBar.eval_confidence,
          eval_exit_risk:        rawBar.eval_exit_risk,
          position_id:           rawBar.position_id || rawBar.eval_position_id,
          decision:              rawBar.decision || rawBar.eval_decision || rawBar.signal,
        }));

        setCleanbars(outputArr);
        setLiveBars(outputArr);

        let cb = predictionAccuracy(config.ACCURACY_WINDOW, 1, outputArr);
        setBars(cb);
        dispatch(addPrediction(outputArr));

        if (outputArr.length > 0) {
          const lastBar = outputArr[outputArr.length - 1];
          setCurrentPrice(lastBar.close);
          setDateInfo(prev => ({
            ...prev,
            symbol: lastBar.symbol || "SPY",
            displayDate: new Date(dateParam).toLocaleDateString("en-US", {
              month: "long", day: "numeric", year: "numeric",
            }),
          }));
        }

        // Fetch orders for this date — cache-first (warmed by TradeDashboard if user
        // navigated from there; falls back to a direct /orders fetch on cold load).
        const rawData = await fetchOrdersByDate(dateParam);

        setRawOrders(rawData);
        dispatch(addOrder(rawData));

        setLoading(false);
      } catch (err) {
        console.error("Error loading historical data:", err);
        setError("Failed to load data for this date");
        setLoading(false);
      }
    };

    loadHistoricalData();
  }, [dateParam, dispatch]);

  // -------------------------------------------------------------------------
  // CSV EXPORT HANDLERS — unchanged
  // -------------------------------------------------------------------------
  const handleExportPredictions     = () => liveBars.length    > 0 && exportPredictions(liveBars, dateParam);
  const handleExportActivities      = () => activities.length  > 0 && exportActivities(activities, dateParam);
  const handleExportClosedPositions = () => activities.length  > 0 && exportGeneric(closedPositions, dateParam, "closed_positions");
  const handleExportActivePositions = () => activities.length  > 0 && exportGeneric(activePositions, dateParam, "active_positions");
  const handleExportOrders          = () => rawOrders.length   > 0 && exportOrders(rawOrders, dateParam);

  // -------------------------------------------------------------------------
  // DERIVED STATE — unchanged
  // -------------------------------------------------------------------------
  const activities = useMemo(() => {
    if (!rawOrders || rawOrders.length === 0) return [];
    try {
      const activityArray = [];
      rawOrders.forEach(row => {
        const [activity] = prepareOrder(row);
        if (activity) activityArray.push(activity);
      });
      if (activityArray.length === 0) return [];
      return buildActivities(activityArray);
    } catch (err) {
      console.error("Activity processing error:", err);
      return [];
    }
  }, [rawOrders]);

  // positions — rebuilt via OrderLedgerBuilder (same FIFO engine as PositionLedger)
  // Fixes: partial-fill merging, wrong price source, inverted SHORT P&L
  const positions = useMemo(() => {
    if (!rawOrders || rawOrders.length === 0) return [];
    try {
      const builder = new OrderLedgerBuilder();
      builder.processOrderHistory(rawOrders);
      const fmtHoldMin = (s) => (s != null && s > 0 ? Math.round(s / 60) : 0);
      const mapPos = (raw, isActive) => {
        const isLong = raw.direction === "LONG";
        const liveOrExit = isActive ? currentPrice : (raw.exit_price || 0);
        const unrealizedPL = isActive
          ? (isLong ? (currentPrice - raw.entry_price) * raw.qty_open : (raw.entry_price - currentPrice) * raw.qty_open)
          : (raw.pnl || 0);
        let exitType = "Open";
        if (!isActive) {
          const ot = (raw.exit_order_type || "").toLowerCase();
          if (ot === "limit") exitType = "Target Fill";
          else if (ot === "stop" || ot === "stop_limit") exitType = "Stop Hit";
          else exitType = "Market Close";
        }
        const stableId = raw.entry_submitted_at || raw.entry_filled_at || String(Math.random());
        return {
          id: stableId, a_id: stableId, group: stableId,
          symbol: raw.symbol, side: isLong ? "buy" : "sell", isLong,
          qty: raw.quantity || raw.qty_open || 0,
          open_qty: isActive ? (raw.qty_open || 0) : 0,
          entryPrice: raw.entry_fill_price || raw.entry_price || 0,
          exitPrice:  raw.exit_fill_price  || raw.exit_price  || 0,
          currentPrice: liveOrExit, unrealizedPL, pnl: raw.pnl || 0,
          entryTime: raw.entry_submitted_at || raw.entry_filled_at || null,
          exitTime:  isActive ? null : (raw.exit_filled_at || null),
          holdTimeMin: fmtHoldMin(raw.hold_time_seconds),
          exitType, isWin: unrealizedPL > 0,
          target: raw.tp_limit || 0, stop: raw.sl_stop || 0, rrRatio: raw.rr || 0, tax: 0,
          entry_filled_at: raw.entry_filled_at, exit_filled_at: raw.exit_filled_at,
          entry_fill_price: raw.entry_fill_price || raw.entry_price,
          exit_fill_price:  raw.exit_fill_price  || raw.exit_price,
          direction: raw.direction, asset_class: raw.asset_class,
          hold_time_seconds: raw.hold_time_seconds, activities: [],
        };
      };
      const activeMapped = [
        ...Object.values(builder.longInventory).flat(),
        ...Object.values(builder.shortInventory).flat(),
      ].filter(lot => (lot.qty_open || 0) > 0).map(lot => mapPos(lot, true));
      const closedMapped = builder.closedPositions.map(p => mapPos(p, false));
      return [...activeMapped, ...closedMapped];
    } catch (err) {
      console.error("[DDD] positions build error:", err);
      return [];
    }
  }, [rawOrders, currentPrice]);

  const metrics = useMemo(() => {
    if (!positions || positions.length === 0) {
      return { dayPL: 0, realizedPL: 0, unrealizedPL: 0, activePositions: 0,
               closedTrades: 0, winRate: 0, directionalAccuracy: 0,
               avgHoldTime: 0, bestTrade: 0, worstTrade: 0, totalFees: 0 };
    }
    const closedPos  = positions.filter(p => Number(p?.open_qty ?? 0) === 0);
    const activePos  = positions.filter(p => Number(p?.open_qty ?? 0) > 0);

    // Equity-only closed positions — one row per PROCEED decision
    // This is the authoritative set for trade-level win/loss counting
    const closedEquity = closedPos.filter(p => !p.asset_class || p.asset_class === "us_equity");

    const realizedPL   = closedPos.reduce((sum, p) => sum + (parseFloat(p.unrealizedPL) || 0), 0);
    const unrealizedPL = activePos.reduce((sum, p)  => sum + (parseFloat(p.unrealizedPL) || 0), 0);
    const dayPL        = realizedPL + unrealizedPL;
    const totalFees    = positions.reduce((sum, p) => sum + Math.abs(parseFloat(p.tax) || 0), 0);

    // Win rate — based on combined net P&L per trade (equity wins that option erased still = loss)
    // We use equity rows as the trade spine; net P&L is computed in TradeStoryView using combinedPnl
    // Here we approximate: equity isWin as directional accuracy, net isWin for win rate
    const directionalWins = closedEquity.filter(p => p.isWin).length;
    const directionalAccuracy = closedEquity.length > 0
      ? parseFloat(((directionalWins / closedEquity.length) * 100).toFixed(0))
      : 0;

    // Net win rate — equity wins that were erased by options still count as losses
    // Approximated from closedEquity unrealizedPL for now (will be overridden by combinedPnl in pills)
    const winRate = directionalAccuracy; // refined below once combinedPnl is available in TradeStoryView

    const holdSecs = closedEquity
      .map(p => p.hold_time_seconds != null ? p.hold_time_seconds : (p.holdTimeMin || 0) * 60)
      .filter(s => s > 0 && s <= 23400);
    const avgHoldTime = holdSecs.length > 0
      ? Math.round(holdSecs.reduce((s, v) => s + v, 0) / holdSecs.length / 60)
      : 0;
    const pls = closedEquity.map(p => parseFloat(p.unrealizedPL) || 0);
    return {
      dayPL, realizedPL, unrealizedPL,
      activePositions: activePos.length,
      closedTrades: closedEquity.length,   // equity count = unique trades
      winRate, directionalAccuracy, avgHoldTime,
      bestTrade:  pls.length > 0 ? Math.max(...pls) : 0,
      worstTrade: pls.length > 0 ? Math.min(...pls) : 0,
      totalFees,
    };
  }, [positions]);

  const closedPositions = useMemo(() => positions.filter(p => Number(p?.open_qty ?? 0) === 0), [positions]);
  const activePositions  = useMemo(() => positions.filter(p => Number(p?.open_qty ?? 0) > 0),  [positions]);

  // ledgerPositions — all rows (equity + options) from OrderLedgerBuilder
  // Split into equity/options so matching logic never double-counts
  const ledgerPositions = useMemo(() => {
    if (!rawOrders || rawOrders.length === 0) return [];
    try {
      const builder = new OrderLedgerBuilder();
      const view = builder.processOrderHistory(rawOrders);
      return [...(view.stocks || []), ...(view.options || [])];
    } catch (err) {
      console.error("ledgerPositions build error:", err);
      return [];
    }
  }, [rawOrders]);

  // Equity-only rows — one per PROCEED decision, used for greedy matching + fill rate
  const ledgerEquityPositions = useMemo(
    () => ledgerPositions.filter(lp => !lp.option_type),
    [ledgerPositions]
  );

  // Options-only rows — joined to equity by timestamp for combined P&L display
  const ledgerOptionsPositions = useMemo(
    () => ledgerPositions.filter(lp => !!lp.option_type),
    [ledgerPositions]
  );

  // -------------------------------------------------------------------------
  // HANDLERS — unchanged
  // -------------------------------------------------------------------------
  const handleBack = () => {
    // Push to /trading with returnDate so TradeDashboard can restore scroll
    // position to the row the user was viewing. This avoids a full remount
    // that window.history.back() would trigger with stale cache state.
    history.push("/trading", { returnDate: dateParam });
  };

  const handlePositionSelect    = pos  => { setSelectedPosition(pos);   setShowPositionModal(true); };
  const handleEvaluationSelect  = evalItem => { setSelectedEvaluation(evalItem); setShowEvalModal(true); };
  const handlePositionModalClose = ()  => { setShowPositionModal(false); setSelectedPosition(null);  };
  const handleViewFullStory      = pos => { setSelectedPosition(pos);   setShowPositionModal(true); };

  const filterChartDataForPosition = (chartData, position) => {
    if (!position || !chartData || chartData.length === 0) return [];
    const entryTime = new Date(position.entryTime).getTime();
    const exitTime  = position.exitTime ? new Date(position.exitTime).getTime() : Date.now();
    return chartData.filter(bar => {
      const t = new Date(bar.t || bar.timestamp).getTime();
      return t >= entryTime && t <= exitTime;
    });
  };

  const filterEvaluationsForPosition = (chartData, position) => {
    if (!position || !chartData || chartData.length === 0) return [];
    const entryTime = new Date(position.entryTime).getTime();
    const exitTime  = position.exitTime ? new Date(position.exitTime).getTime() : Date.now();
    return chartData.filter(bar => {
      const t = new Date(bar.t || bar.timestamp).getTime();
      return t >= entryTime && t <= exitTime && bar.evaluation;
    });
  };

  const handleDataFromPC = (window, scale) => {
    if (window > 10 && cleanbars.length > 0) {
      setBars(predictionAccuracy(window, scale, cleanbars));
    }
  };

  // =========================================================================
  // RENDER
  // =========================================================================

  // Loading state
  if (loading) {
    return (
      <div style={{
        background: C.bg, minHeight: "100vh",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <style>{CSS_ANIMATIONS}</style>
        <div style={{ textAlign: "center" }}>
          <Orb color={C.cyan} size={12} speed="1.5s" />
          <div style={{
            fontFamily: "'DM Mono',monospace", fontSize: 11,
            color: C.meta, marginTop: 20, letterSpacing: "1px",
          }}>
            LOADING SESSION DATA...
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh" }}>
        <style>{CSS_ANIMATIONS}</style>
        <SessionContextHeader sessionSummary={sessionSummary} dateParam={dateParam} onBack={handleBack} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
          <div style={{
            background: C.bg1, border: `1px solid ${C.red}30`,
            borderRadius: 12, padding: "32px 40px", textAlign: "center", maxWidth: 400,
          }}>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.red, letterSpacing: "1px", marginBottom: 12 }}>
              DATA ERROR
            </div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.body }}>
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'DM Mono',monospace" }}>
      <style>{CSS_ANIMATIONS}</style>

      {/* Context header — breadcrumb with session summary from TradeDashboard */}
      <SessionContextHeader sessionSummary={sessionSummary} dateParam={dateParam} liveBars={liveBars} onBack={handleBack} />

      {/* Market Chart — unchanged component, reskinned wrapper */}
      {liveBars.length > 0 && (
        <div style={{ padding: "0 32px", background: C.bg1, borderBottom: `1px solid ${C.border}` }}>
          <MarketChart
          data={liveBars}
          selectedPosition={selectedPosition}
          defaultChartType="area"
          defaultWindow="full"
        />
        </div>
      )}

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px 80px" }}>

        {/* Zone 1 — Daily Scorecard */}
        <DailyScorecard liveBars={liveBars} metrics={metrics} />

        {/* Zone 2 — Session Intelligence Summary */}
        {liveBars.length > 0 && (
          <SessionIntelligenceSummary liveBars={liveBars} metrics={metrics} />
        )}

        {/* Zone 3 — Story View (Story/Detail toggle) */}
        {liveBars.length > 0 && (
          <StoryViewWrapper
            liveBars={liveBars}
            ledgerPositions={ledgerPositions}
            ledgerEquityPositions={ledgerEquityPositions}
            ledgerOptionsPositions={ledgerOptionsPositions}
            positions={positions}
            selectedPosition={selectedPosition}
            onEvalSelect={handleEvaluationSelect}
            onPositionSelect={handlePositionSelect}
          />
        )}

        {/* Zone 4 — Architecture in Action */}
        {liveBars.length > 0 && (
          <ArchitectureActivityPanel liveBars={liveBars} />
        )}

        {/* Zone 7 — Memory Intelligence */}
        {liveBars.length > 0 && (
          <MemoryIntelligencePanel
            liveBars={liveBars}
            ledgerPositions={ledgerPositions}
          />
        )}

        {/* Zone 5 — Execution Quality */}
        {closedPositions.length > 0 && (
          <div style={{ marginBottom: 32 }}>
            <Eyebrow>Execution Quality</Eyebrow>


            {/* Hold time summary strip */}
            {(() => {
              const holds = closedPositions.map(p => p.holdTimeMin).filter(Boolean).sort((a, b) => a - b);
              if (holds.length === 0) return null;
              const min = holds[0];
              const max = holds[holds.length - 1];
              const med = holds[Math.floor(holds.length / 2)];
              return (
                <div style={{
                  display: "flex", gap: 32, padding: "16px 24px",
                  background: C.bg1, border: `1px solid ${C.border}`,
                  borderRadius: 10, marginBottom: 16, flexWrap: "wrap",
                }}>
                  {[
                    { label: "SHORTEST",          val: `${min}m`, color: C.cyan  },
                    { label: "MEDIAN",             val: `${med}m`, color: C.green },
                    { label: "LONGEST",            val: `${max}m`, color: C.amber },
                    { label: "ALL CLOSED SAME DAY", val: "✓",      color: C.green },
                  ].map(({ label, val, color }) => (
                    <div key={label}>
                      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta, letterSpacing: "1px", marginBottom: 4 }}>
                        {label}
                      </div>
                      <div style={{ fontFamily: "'DM Mono',monospace", fontWeight: 500, fontSize: 20, color }}>
                        {val}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            <ClosedPositions
              positions={closedPositions}
              totalRealized={metrics.realizedPL}
              tradeCount={metrics.closedTrades}
            />
          </div>
        )}

        {/* Position Ledger */}
        <div style={{ marginBottom: 32 }}>
          <Eyebrow>Position Ledger</Eyebrow>
          <PositionLedger rawOrders={rawOrders} proceedEvals={[]} onOptionClick={() => {}} />
        </div>

        {/* Zone 6 — Memory Trail */}
        {liveBars.length > 0 && (
          <MemoryTrailCard liveBars={liveBars} sessionCount={0} />
        )}

        {/* No data */}
        {activities.length === 0 && !loading && (
          <div style={{
            background: C.bg1, border: `1px solid ${C.border}`,
            borderRadius: 14, padding: "64px", textAlign: "center",
          }}>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta, letterSpacing: "1.5px", marginBottom: 12 }}>
              NO TRADING ACTIVITY
            </div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.body }}>
              No trades were executed on this date
            </div>
          </div>
        )}

      </div>

      {/* Modals — unchanged */}
      <EvaluationDetailModal
        evaluation={selectedEvaluation}
        show={showEvalModal}
        onHide={() => setShowEvalModal(false)}
      />

      <PositionDetailModal
        position={selectedPosition}
        chartData={selectedPosition ? filterChartDataForPosition(liveBars, selectedPosition) : []}
        evaluations={selectedPosition ? filterEvaluationsForPosition(liveBars, selectedPosition) : []}
        show={showPositionModal}
        onHide={handlePositionModalClose}
        onViewFullStory={handleViewFullStory}
      />
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px 80px" }}>
      <MemoryConfidenceBar 
      liveBars={liveBars}
      mode="session"
      compact={false}
        />
      
      <ModelConsensusPanel 
      liveBars={liveBars}
        />
      
      <RegimeDecisionTally
        liveBars={liveBars}
        compact={false}
        closedLedgerPositions={ledgerPositions.filter(lp => lp.status === "closed" && !lp.option_type)}
      />
        </div>
    </div>
  );
};

export default DailyDetailDashboard;